/*!
 * Phantom Swap SDK v2
 *
 * Loaded from theme.liquid on every Shopify page. Behaviour:
 *
 *  1. On first hit with `?utm_phantom=<token>` (or `fbclid` + valid token),
 *     stash the token in sessionStorage so subsequent in-store navigation
 *     keeps the swap active without needing the UTM on every URL.
 *
 *  2. On a product page, fetch the REAL branded content from
 *     `/api/phantom/verify-swap` and patch the DOM in place (title, desc,
 *     images, OG meta).
 *
 *  3. On collection / home / search / any list page, scan product cards,
 *     batch-fetch `/api/phantom/verify-swap-batch` and patch each card's
 *     title + thumbnail + price label.
 *
 *  4. Always reveal the body within 2.5s as failsafe — never let cloaking
 *     prevent the page from rendering.
 *
 * ES5-safe, no transpile. Served raw (no minify/bundle) so it MUST stay a
 * single self-contained IIFE with no imports.
 *
 * Module map (top → bottom):
 *   - Bootstrap: script-tag config (BASE/API/SHOP), helpers
 *   - Animate guard + loader teardown (loader class is injected by
 *     theme-injector; this script only tears it down)
 *   - Token resolution + visual debug panel
 *   - Page-type detection
 *   - Shared swap helpers (stripLazyAttrs, swapImg, swapLogoImg, plain)
 *   - Single-product (PDP) swap: title/desc/images/gallery/bundles/sticky ATC
 *   - List swap: collection / cart / recommendation cards
 *   - Header logo + ad banner
 *   - Network: snapshot cache + verify-swap[-batch] fetches
 *   - Observers: card rescan + src guard (re-heal lazy-loader overwrites)
 *   - fetch() hijack for recommendation/section/cart HTML
 *   - Storefront tweaks: footer brand, PDP accordions, catalog link,
 *     direct checkout, card badges
 *   - Dispatch + startup
 */
(function () {
  // ── Live-visitor beacon ──────────────────────────────────────────────
  // Fires on EVERY storefront pageview — placed before the PHANTOM_CLEAN /
  // token guards so it captures all real traffic (cloaked AND clean/decoy
  // views), exactly once per pageview. Self-contained: resolves its own
  // script tag + base/shop independently of the engine below. Fire-and-forget
  // 1x1 pixel to guardians-ai.com → records a StoreVisit → feeds /dashboard/live.
  try {
    if (!window.__PHANTOM_BEACON) {
      window.__PHANTOM_BEACON = true;
      var __bs = document.currentScript;
      if (!__bs) {
        var __all = document.getElementsByTagName("script");
        for (var __i = 0; __i < __all.length; __i++) {
          if (/phantom-swap\.js/.test(__all[__i].src)) { __bs = __all[__i]; break; }
        }
      }
      var __base = (__bs && __bs.getAttribute("data-base")) || "";
      if (!__base && __bs && __bs.src) { try { __base = new URL(__bs.src).origin; } catch (_e) {} }
      __base = (__base || "").replace(/\/+$/, "");
      var __shop = (__bs && __bs.getAttribute("data-shop")) || location.hostname;
      if (__base && __shop) {
        var __ref = "", __path = "";
        try { __ref = encodeURIComponent((document.referrer || "").slice(0, 200)); } catch (_e) {}
        try { __path = encodeURIComponent((location.pathname || "").slice(0, 200)); } catch (_e) {}
        var __px = new Image();
        __px.src =
          __base + "/api/track/visit?shop=" + encodeURIComponent(__shop) +
          (__ref ? "&ref=" + __ref : "") + (__path ? "&path=" + __path : "") +
          "&_=" + (+new Date());
      }
    }
  } catch (_e) {}
  // ─────────────────────────────────────────────────────────────────────

  if (window.PHANTOM_CLEAN) {
    try {
      var h = document.documentElement;
      if (h) h.className = (h.className || "").replace(/\bphantom-loading\b/g, "").replace(/\s+/g, " ").replace(/^\s+|\s+$/g, "");
    } catch (_e) {}
    return;
  }
  var script = document.currentScript;
  if (!script) {
    var all = document.getElementsByTagName("script");
    for (var s = 0; s < all.length; s++) {
      if (/phantom-swap\.js/.test(all[s].src)) { script = all[s]; break; }
    }
  }

  var BASE = (script && script.getAttribute("data-base")) || "";
  if (!BASE && script && script.src) {
    try { BASE = new URL(script.src).origin; } catch (_e) { BASE = ""; }
  }
  var SHOP = (script && script.getAttribute("data-shop")) || location.hostname;
  // Normalized API origin (no trailing slash) — used to build every
  // /api/phantom/* URL. Empty when BASE is empty, but in that case the
  // token/base guard below aborts before any fetch runs.
  var API = BASE.replace(/\/+$/, "");
  var STORAGE_KEY = "phantom_swap_token";
  // Global circuit breaker. A theme reveal/slider tween (secondary.js) on the
  // heaviest PDPs can re-render product nodes ~60x/s; phantom's reactive
  // observers + poll then saturate the main thread and FREEZE the page. A
  // watchdog (installed in startup) measures event-loop lag and flips this to
  // true when the thread is sustainedly blocked — every heavy reactive callback
  // checks it and bails, so phantom can NEVER fight an animation into a freeze.
  // The initial branded swap completes in ~1s (snapshot, synchronous), well
  // before any storm, so content is already branded when we halt.
  var PHANTOM_HALT = false;
  // Branded footer/PDP copy data-driven por loja (vem de /api/phantom/branding →
  // data.copy). Substitui o texto que antes era cravado p/ a Metzingen e gated por
  // shop (tdss3x). Preenchido em swapHeaderLogo; aplicado por swapFooterBrand /
  // swapPdpAccordions. Null = loja sem copy → não mexe no footer (não vaza marca).
  var __brandCopy = null;
  var __footerWired = false; // observer/interval do footer instalados 1x
  var __accWired = false;    // observer/interval dos acordeões instalados 1x

  function strip(el, cls) {
    if (!el) return;
    el.className = (el.className || "")
      .replace(new RegExp("\\b" + cls + "\\b", "g"), "")
      .replace(/\s+/g, " ")
      .replace(/^\s+|\s+$/g, "");
  }

  // Install loader CSS + overlay as the very first thing — runs BEFORE the
  // browser paints any of the cloaked content. Without this the visitor would
  // see the generic version flash for a few hundred ms before the JS swaps
  // it in (the "flick" people complain about).
  // Force-reveal any element the theme keeps hidden behind its scroll
  // animation classes (animate--hidden / animate-item etc). The theme's
  // IntersectionObserver is supposed to remove these on first paint, but
  // when our scripts mutate the DOM (replaceChild for sections, etc) the
  // observer never sees the element re-enter, so the cards stay invisible.
  // We install a stylesheet that wins regardless.
  function installAnimateGuard() {
    try {
      if (document.getElementById("phantom-animate-guard")) return;
      var st = document.createElement("style");
      st.id = "phantom-animate-guard";
      // Cover EVERY shape of the theme's hide rule. The most important
      // pattern is the parent-child selector
      // ".animate-section.animate--hidden .animate-item { opacity: 0 }"
      // injected inline in the HTML head — our previous guard targeted
      // only direct ".animate--hidden" nodes and lost the specificity
      // battle. The selectors below match (a) any element with the class
      // anywhere in its name, (b) every descendant of a hidden section,
      // and (c) the bare class itself. All combined with !important + a
      // huge cascade win via :where() wrapping unsupported here, so we
      // rely on `*` specificity boosts to override.
      st.textContent = [
        ".animate-section.animate--hidden .animate-item,",
        ".animate-section.animate--hidden .animate-item--child,",
        ".animate-section.animate--hidden *,",
        "[class*=\"animate-section\"].animate--hidden *,",
        ".animate--hidden,",
        ".animate-item.animate--hidden,",
        ".animate-item--child.animate--hidden,",
        "[class*=\"animate--hidden\"],",
        "[class*=\"animate--hidden\"] *,",
        "[class*=\"animate--hidden\"] .animate-item,",
        "[class*=\"animate--hidden\"] .animate-item--child{",
        "  opacity:1 !important;",
        "  visibility:visible !important;",
        "  transform:none !important;",
        "  pointer-events:auto !important;",
        "  animation:none !important;",
        "  transition:none !important;",
        "}",
        // Belt-and-suspenders for known section ids that ship hidden
        "section[id*='collection-product-grid'].animate-section,",
        "section[id*='collection-product-grid'] .animate-item,",
        ".section-template--animation,",
        ".animate-section{",
        "  opacity:1 !important;",
        "  visibility:visible !important;",
        "}",
      ].join("\n");
      (document.head || document.documentElement).appendChild(st);
      // Also strip the class on every element that has any
      // animate--hidden variant. Runs in 5 sweeps to catch lazy DOM.
      function strip() {
        try {
          // Anything that has 'animate--hidden' anywhere in its class.
          var nodes = document.querySelectorAll("[class*='animate--hidden']");
          for (var i = 0; i < nodes.length; i++) {
            var n = nodes[i];
            try { n.classList.remove("animate--hidden"); } catch (_e) {}
            // Direct style reset in case CSS specificity still loses.
            try {
              n.style.opacity = "1";
              n.style.visibility = "visible";
              n.style.transform = "none";
            } catch (_e) {}
          }
        } catch (_e) {}
      }
      strip();
      setTimeout(strip, 50);
      setTimeout(strip, 200);
      setTimeout(strip, 600);
      setTimeout(strip, 1500);
      setTimeout(strip, 3000);
    } catch (_e) {}
  }

  // NOTE: the loading overlay/shield used to be created here
  // (`installLoaderShield`), but it was disabled on 2026-05-28 because it
  // hung mobile visitors on a black "ERLEBNIS WIRD GELADEN" screen. The
  // anti-FOUC `html.phantom-loading` class is now injected by the
  // theme-injector inline script instead; the teardown below strips it.

  function forceRepaint() {
    try {
      var b = document.body;
      if (b) {
        var d = b.style.display;
        b.style.display = "none";
        b.offsetHeight; // force repaint/reflow
        b.style.display = d;
      }
    } catch (_e) {}
  }

  function tearDownLoader() {
    // IMMEDIATELY reveal the body — never gate this on image decoding
    // or anything else. The shield is just a polish layer; if we trip
    // on an unrelated exception below, the visitor still sees the page.
    try { strip(document.documentElement, "phantom-loading"); } catch (_e) {}
    try { if (document.body) strip(document.body, "phantom-cloaked-pending"); } catch (_e) {}
    try {
      var overlay = document.getElementById("phantom-loader-overlay");
      if (overlay) {
        function settle() {
          try {
            overlay.className = "phantom-fade-out";
            setTimeout(function () { overlay.parentNode && overlay.parentNode.removeChild(overlay); }, 280);
            var s = document.getElementById("phantom-loader-style");
            if (s) setTimeout(function () { s.parentNode && s.parentNode.removeChild(s); }, 320);
          } catch (_e) {}
        }
        // Try to wait for swapped product/logo images to decode so the
        // fade-out doesn't flash the original src. But hard-cap the wait
        // to 600ms so we never block forever.
        var imgs = document.querySelectorAll(
          ".product__media img,.product-gallery img,.product__photo img,.product-single__photo img,.product__media-item img," +
          ".header__heading-logo,.header__heading-link img,.header__logo img"
        );
        var pending = 0;
        for (var i = 0; i < imgs.length; i++) {
          var img = imgs[i];
          if (img.tagName !== "IMG") continue;
          if (img.complete && img.naturalWidth > 0) continue;
          pending++;
          (function (im) {
            function go() { im.removeEventListener("load", go); im.removeEventListener("error", go); if (--pending <= 0) settle(); }
            im.addEventListener("load", go);
            im.addEventListener("error", go);
          })(img);
        }
        if (pending === 0) settle();
        setTimeout(function () { if (overlay.parentNode) settle(); }, 600);
      }
      var injectScript = document.getElementById("phantom-init");
      if (injectScript && injectScript.parentNode) injectScript.parentNode.removeChild(injectScript);
    } catch (_e) {}
    forceRepaint();
  }

  // Failsafe — body always reveals within 600ms, no exceptions. We
  // also unconditionally tear down BOTH the loading class AND the
  // overlay element every 200ms for the first 2s. Stripping just the
  // class wasn't enough — the absolutely-positioned overlay div stays
  // on top of the page even after the body is visible, so the user
  // sees a permanent "loading" screen.
  function killLoader() {
    try { strip(document.documentElement, "phantom-loading"); } catch (_e) {}
    // Also strip the body visibility:hidden class — without this, if the full
    // tearDownLoader path never completes the body stays invisible (a white,
    // tap-swallowing block). killLoader must clear BOTH FOUC classes.
    try { if (document.body) strip(document.body, "phantom-cloaked-pending"); } catch (_e) {}
    try {
      var o = document.getElementById("phantom-loader-overlay");
      if (o && o.parentNode) o.parentNode.removeChild(o);
    } catch (_e) {}
    try {
      var s = document.getElementById("phantom-loader-style");
      if (s && s.parentNode) s.parentNode.removeChild(s);
    } catch (_e) {}
    forceRepaint();
  }
  var failsafe = setTimeout(tearDownLoader, 600);
  for (var fsI = 1; fsI <= 10; fsI++) {
    (function (delay) {
      setTimeout(killLoader, delay);
    })(fsI * 200);
  }
  try {
    window.addEventListener("load", function () { try { tearDownLoader(); } catch (_e) {} }, { once: true });
  } catch (_e) {}
  function done() { clearTimeout(failsafe); tearDownLoader(); }

  // ---------- token resolution ----------
  function resolveToken() {
    var params;
    try { params = new URLSearchParams(location.search); }
    catch (_e) { return null; }
    // Dashboard preview: the CLEAN iframe shares sessionStorage with the AD
    // iframe (same proxy origin), so a token set by the AD side would leak in
    // and wrongly brand the organic preview. An explicit clean-preview flag
    // forces the organic view and clears any leaked token.
    if (params.get("phantom_preview") === "clean") {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) {}
      return null;
    }
    var t = params.get("utm_phantom");
    if (t) {
      // Persist for the rest of this browser session so internal nav keeps
      // showing the branded version without needing the UTM on every URL.
      try { sessionStorage.setItem(STORAGE_KEY, t); } catch (_e) {}
      return t;
    }
    // No URL token — check session.
    try {
      var stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) return stored;
    } catch (_e) {}
    return null;
  }

  // ---------- mobile-friendly visual debug panel ----------
  // Activate with `?phantom_debug=1` in the URL. Renders an on-page
  // overlay so the visitor can screenshot it without needing DevTools.
  var DBG_ON = false;
  try {
    DBG_ON = new URLSearchParams(location.search).get("phantom_debug") === "1";
  } catch (_e) {}
  var DBG_BODY = null;
  function dlog() {
    if (!DBG_ON) return;
    var args = [].slice.call(arguments);
    var msg = args
      .map(function (a) {
        if (typeof a === "string") return a;
        try { return JSON.stringify(a); } catch (_e) { return String(a); }
      })
      .join(" ");
    try { console.log("[phantom]", msg); } catch (_e) {}
    if (!DBG_BODY && DBG_ON) {
      try {
        var panel = document.createElement("div");
        panel.id = "phantom-debug-panel";
        panel.style.cssText = [
          "position:fixed",
          "right:6px",
          "bottom:6px",
          "left:6px",
          "max-height:42vh",
          "z-index:2147483646",
          "background:#0a0f14",
          "color:#7CFF4F",
          "font:11px ui-monospace,Menlo,monospace",
          "padding:8px 10px",
          "border:1px solid rgba(124,255,79,.45)",
          "border-radius:10px",
          "overflow:auto",
          "white-space:pre-wrap",
          "word-break:break-word",
        ].join(";");
        var head = document.createElement("div");
        head.style.cssText =
          "display:flex;justify-content:space-between;color:#fff;font-weight:700;border-bottom:1px solid rgba(124,255,79,.2);padding-bottom:4px;margin-bottom:4px";
        head.innerHTML =
          '<span>phantom debug</span><span style="cursor:pointer;color:#888" id="phantom-debug-close">✕</span>';
        panel.appendChild(head);
        DBG_BODY = document.createElement("div");
        panel.appendChild(DBG_BODY);
        var attach = function () {
          if (document.body) {
            document.body.appendChild(panel);
            var c = document.getElementById("phantom-debug-close");
            if (c) c.onclick = function () { panel.style.display = "none"; };
          } else {
            setTimeout(attach, 50);
          }
        };
        attach();
      } catch (_e) {}
    }
    if (DBG_BODY) {
      var line = document.createElement("div");
      var ts = new Date().toISOString().slice(11, 23);
      line.textContent = ts + " " + msg.slice(0, 600);
      line.style.cssText = "padding:1px 0;border-bottom:1px dashed rgba(124,255,79,.06)";
      DBG_BODY.appendChild(line);
      while (DBG_BODY.childNodes.length > 200) DBG_BODY.removeChild(DBG_BODY.firstChild);
      DBG_BODY.parentElement.scrollTop = DBG_BODY.parentElement.scrollHeight;
    }
  }
  // Catch any uncaught exception so the panel shows it.
  try {
    window.addEventListener("error", function (e) {
      dlog("WINDOW ERROR", (e && e.message) || String(e), (e && e.filename) || "", "line", e && e.lineno);
    });
    window.addEventListener("unhandledrejection", function (e) {
      dlog("UNHANDLED REJECTION", e && e.reason && (e.reason.message || String(e.reason)));
    });
  } catch (_e) {}
  dlog("boot path=" + location.pathname + " ua=" + (navigator.userAgent || "").slice(0, 80));

  // ALWAYS install the animate-guard, even on the organic path —
  // ensures the theme's lazy-reveal animation never traps cards in a
  // permanently hidden state when other scripts touch the DOM.
  installAnimateGuard();
  dlog("after installAnimateGuard");

  var TOKEN = resolveToken();
  dlog("TOKEN=", TOKEN ? TOKEN.slice(0, 16) + "…" : null, "BASE=", BASE);
  if (!TOKEN || !BASE) { dlog("ABORT: no token/base"); done(); return; }

  // (Loader shield disabled 2026-05-28 — see note above tearDownLoader.)

  // Signal to the inline fallback (theme-injector) that the external engine
  // is alive and owns card/title/image swapping. The inline fallback checks
  // this flag and stops its own 1500ms loop + observer so the two engines
  // never write different image assignments to the same card (flicker +
  // double network). It is set only AFTER the token/base guard passed, so on
  // the abort path the inline fallback keeps running as the sole engine.
  try { window.PHANTOM_ENGINE_READY = true; } catch (_e) {}

  // ---------- page-type detection ----------
  var path = location.pathname;
  var productMatch = path.match(/\/products\/([^\/?#]+)/);
  var collectionMatch = path.match(/^\/collections\/([^\/?#]+)/);
  var isIndex = path === "/" || path === "";
  var isSearch = path === "/search";
  var isList = collectionMatch || isIndex || isSearch || /^\/products$/.test(path);

  // ---------- shared swap helpers ----------
  // Drop every lazy-loader hook the theme placed on an <img> so our new
  // `src` wins and isn't overwritten when the element enters the viewport.
  function stripLazyAttrs(img) {
    try {
      img.removeAttribute("srcset");
      img.removeAttribute("sizes");
      img.removeAttribute("data-srcset");
      img.removeAttribute("data-src");
      img.removeAttribute("data-original");
      img.removeAttribute("data-bgset");
      img.removeAttribute("data-image");
    } catch (_e) {}
  }

  // Logo-specific swap: keep the natural aspect ratio of the new logo
  // (often a wide wordmark) inside whatever slot the theme renders. We
  // strip lazy-loader hooks so the new src wins, set `object-fit: contain`
  // and let the slot's CSS height drive the size. The theme's `logo_width`
  // setting still wraps the image, so contain-fit shrinks the wordmark to
  // fit without stretching.
  function swapLogoImg(img, src, alt) {
    if (!img || !src) return;
    try { img.loading = "eager"; } catch (_e) {}
    stripLazyAttrs(img);
    img.src = src;
    if (alt) img.alt = alt;
    try {
      if (img.getAttribute("width")) img.removeAttribute("width");
      if (img.getAttribute("height")) img.removeAttribute("height");
      img.setAttribute("data-phantom-logo", "1");
      // Force override the broken inline CSS from theme.liquid
      img.style.setProperty("content", "url(" + src + ")", "important");
      
      var oldSt = document.getElementById("phantom-logo-style");
      if (oldSt && oldSt.innerHTML.indexOf("outletcity_logo.png") !== -1) {
        oldSt.innerHTML = oldSt.innerHTML.replace(/url\([^)]+\)/g, 'url("' + src + '")');
      }
      
      if (!document.getElementById("phantom-logo-style-swap")) {
        var st = document.createElement("style");
        st.id = "phantom-logo-style-swap";
        st.textContent = [
          // Desktop: cap by WIDTH so the wordmark never overflows the
          // header slot, then let height follow proportionally. The
          // square logo art is 500x500 with comfortable whitespace, so
          // a width cap renders cleanly without clipping.
          'img[data-phantom-logo]{',
          '  object-fit:contain !important;',
          '  object-position:center !important;',
          '  width:auto !important;',
          '  height:auto !important;',
          '  max-width:220px !important;',
          '  max-height:50px !important;',
          '  display:block !important;',
          '  visibility:visible !important;',
          '  opacity:1 !important;',
          '  margin:0 auto !important;',
          '  float:none !important;',
          '  transform:none !important;',
          '  position:static !important;',
          '}',
          // Parent slot — flex-center everything so the logo sits dead
          // center of the header strip, regardless of theme grid rules.
          '.header__heading,.header__heading-link,.header__logo,.site-header__logo,h1.header__heading{',
          '  max-height:none !important;',
          '  height:auto !important;',
          '  width:auto !important;',
          '  max-width:none !important;',
          '  overflow:visible !important;',
          '  display:flex !important;',
          '  align-items:center !important;',
          '  justify-content:center !important;',
          '  text-align:center !important;',
          '  margin:0 auto !important;',
          '  padding:6px 0 !important;',
          '}',
          // Header section itself — ensure the slot containing the
          // logo aligns to the row center too.
          '.header__heading-wrapper,.header__inline-menu-wrapper,.header__heading-container{',
          '  display:flex !important;',
          '  align-items:center !important;',
          '  justify-content:center !important;',
          '}',
          // Dawn header grid: enforce a clean 3-column layout (left icons,
          // center logo, right cart) and pin the logo to the center cell
          // so it's dead-centered regardless of theme overrides.
          'header.header{',
          '  display:grid !important;',
          '  grid-template-columns:1fr auto 1fr !important;',
          '  align-items:center !important;',
          '  justify-items:center !important;',
          '}',
          'header.header > .header__heading,',
          'header.header > h1.header__heading{',
          '  grid-column:2 !important;',
          '  justify-self:center !important;',
          '  text-align:center !important;',
          '  margin:0 !important;',
          '}',
          // Mobile preserves the same center alignment.
          '@media (max-width:749px){',
          '  header.header{grid-template-columns:auto 1fr auto !important}',
          '  header.header > .header__heading{grid-column:2 !important}',
          '}',
          // (background match dropped — logo is now a transparent PNG,
          //  so the theme's native background passes through cleanly.)
          // Phone — slim cap so the wordmark stays in the header strip
          // and never collides with the menu/cart icons.
          '@media (max-width:749px){',
          '  img[data-phantom-logo]{width:200px !important;max-width:60vw !important;height:auto !important;max-height:120px !important}',
          '  .header__heading,.header__heading-link,.header__logo,.site-header__logo,h1.header__heading{padding:8px 0 !important}',
          '}',
        ].join("\n");
        (document.head || document.documentElement).appendChild(st);
      }
    } catch (_e) {}
    try {
      img.setAttribute("data-phantom-swapped", "1");
      img.setAttribute("data-phantom-src", src);
    } catch (_e) {}
  }

  function swapImg(img, src, alt) {
    if (!img || !src) return;
    // Idempotency gate: if this img already carries our branded src AND the
    // live src still resolves to it, do NOTHING. Every write below is an
    // attribute mutation that re-arms startSrcGuard + the body observers +
    // re-enters the 1000ms poll, so re-writing an already-correct img is the
    // engine of the branded<->clean ping-pong and the CPU thrash. We only
    // touch the DOM when the value genuinely needs to change.
    try {
      if (img.getAttribute("data-phantom-src") === src) {
        var cur = img.getAttribute("src") || img.src || "";
        if (cur.indexOf(src) !== -1) {
          // Already branded and the theme has not reset it — leave it alone.
          if (img.getAttribute("srcset")) { try { img.removeAttribute("srcset"); } catch (_e) {} }
          return;
        }
      }
    } catch (_e) {}
    try { if (img.loading !== "eager") img.loading = "eager"; } catch (_e) {}
    stripLazyAttrs(img);
    if (img.src !== src) img.src = src;
    if (alt && img.alt !== alt) img.alt = alt;
    // Normalize dimensions — branded source images may have different
    // intrinsic dimensions than the clean ones. Without this the layout
    // jumps and the image overflows on mobile.
    try {
      // Drop explicit width/height attrs that the theme placed for the
      // clean image's resolution (e.g. 1024x1024). The slot CSS controls
      // the box; let the browser fit the new image inside it.
      // ONE-TIME layout normalization. Writing these every re-entry is what FROZE
      // the heaviest PDPs (combo-de-3-1 / the prada paquete): re-applying
      // height:100% re-changed the gallery img box, re-triggering secondary.js
      // splide-component.setActiveSlideHeight (offsetHeight read -> --active-slide-
      // height write) + the native slider-component ResizeObserver->initPages
      // forced reflow, in a tight write->read->write reflow loop (native layout
      // work, which is why the setInterval watchdog never fired). We now style each
      // img ONCE and never re-write layout on a theme src-reset, AND drop the
      // explicit height:100% — base.css `.media img{height:100%}` controls the box;
      // the height write was the offsetHeight driver of the loop.
      if (!img.getAttribute("data-phantom-styled")) {
        if (img.getAttribute("width")) img.removeAttribute("width");
        if (img.getAttribute("height")) img.removeAttribute("height");
        img.style.setProperty("object-fit", "contain", "important");
        img.style.setProperty("object-position", "center", "important");
        img.style.setProperty("max-width", "100%", "important");
        img.style.setProperty("width", "100%", "important");
        img.setAttribute("data-phantom-styled", "1");
      }
    } catch (_e) {}
    try {
      img.setAttribute("data-phantom-swapped", "1");
      img.setAttribute("data-phantom-src", src);
    } catch (_e) {}
    try {
      var picture = img.parentElement && img.parentElement.tagName === "PICTURE" ? img.parentElement : null;
      if (picture) {
        var srcs = picture.querySelectorAll("source");
        for (var i = 0; i < srcs.length; i++) {
          srcs[i].srcset = src;
          srcs[i].removeAttribute && srcs[i].removeAttribute("data-srcset");
        }
      }
    } catch (_e) {}
  }

  function plain(html) {
    return String(html).replace(/<[^>]+>/g, "").slice(0, 160);
  }

  // ---------- single-product page swap ----------
  var TITLE_SEL = [
    "h1.product__title",
    "h1[data-product-title]",
    "h1.product-single__title",
    "h1.product-title",
    // Dawn / Horizon: the title container is a div.product__title
    // wrapping <h1 class="h1"> AND a sibling <a class="product__title"><h2>.
    // We need to hit the inner heading(s) directly — never the wrapper —
    // otherwise textContent= nukes the whole sub-tree.
    ".product__title > h1",
    ".product__title > h2",
    ".product__title a > h2",
    ".product__title a > h1",
    ".product-meta__title",
    "h1.h1",
    "h1",
  ].join(",");
  var DESC_SEL = "[data-product-description],.product__description,.product-single__description,.product-description,.product__info .rte,.rte";
  // Main gallery image slots (large viewer).
  var IMG_SEL = [
    ".product__media img",
    ".product__media-item img",
    ".product-gallery img",
    ".product__photo img",
    ".product-single__photo img",
    ".product__media-list img",
    ".product-media img",
    ".product-images img",
    "[data-product-media-id] img",
    "[data-product-media] img",
  ].join(",");

  // Thumbnail strip (mini previews the visitor clicks to navigate the
  // gallery). Dawn/Shrine/Horizon themes use `.thumbnail-list .thumbnail`
  // with the <img> nested inside a <button>.
  var THUMB_SEL = [
    ".thumbnail-list img",
    ".thumbnail-list__item img",
    ".thumbnail img",
    ".thumbnail-slider img",
    ".product-thumbnails img",
    ".product__media-thumb img",
    ".product-gallery__thumb img",
    ".product-single__thumbnails img",
    ".thumbnails img",
    ".product-thumbnail img",
    "button.thumbnail img",
    "[data-thumbnail] img",
    // Bundle apps / variant images
    "form[action*='/cart/add'] img",
    ".product-form img",
    ".variant-wrapper img",
    ".product-variant img",
    ".bundle-product img",
    ".bundle-option img",
    "[data-bundle-product] img"
  ].join(",");

  // Track the active product's branded images so MutationObserver can
  // re-apply them when the theme injects new gallery slides / zoom modal
  // images after the initial paint.
  var activeProductImages = null;

  function ensureGallerySlots(neededCount) {
    // DISABLED — this used to clone gallery slides and REPLACE the theme's
    // <slider-component> custom element so the branded set's extra image(s) got
    // registered. But replacing the custom element re-triggers the theme's
    // secondary.js slider lifecycle, which re-renders the gallery and fires our
    // MutationObserver, which re-runs applyImagesToGallery -> ensureGallerySlots ->
    // replace again: an infinite synchronous loop between phantom-swap.js and
    // secondary.js that FROZE the whole PDP (only on products whose clean gallery
    // has fewer slides than the 4-image branded set, e.g. combo-de-3-1 / the prada
    // paquete). We now leave the slider untouched and just swap the EXISTING slides'
    // images (applyImagesToGallery below) — the visitor sees the clean count of
    // branded images; a missing 4th gallery image is a fair trade for never freezing.
    return;
    try { // eslint-disable-line no-unreachable
      var mediaList = document.querySelector(".product__media-list, ul[id^='Slider-Gallery-'], .product-gallery__list");
      var thumbList = document.querySelector(".thumbnail-list, ul[id^='ThumbnailList-'], .product-gallery__thumbnails");
      
      var slides = mediaList ? mediaList.querySelectorAll("li.product__media-item, li.slider__slide, .product__media-item, .product-gallery__slide") : [];
      var thumbs = thumbList ? thumbList.querySelectorAll("li.thumbnail-list__item, .thumbnail-list__item, .product-gallery__thumbnail") : [];
      
      var hasAppended = false;

      if (mediaList && slides.length > 0 && slides.length < neededCount) {
        for (var i = slides.length; i < neededCount; i++) {
          var lastSlide = slides[slides.length - 1];
          var clone = lastSlide.cloneNode(true);
          
          // Generate unique IDs and data attributes
          var uniqueSuffix = "phantom-clone-" + i;
          if (clone.id) {
            clone.id = clone.id.split("-phantom-clone-")[0] + "-" + uniqueSuffix;
          }
          var oldMediaId = clone.getAttribute("data-media-id");
          var newMediaId = oldMediaId ? oldMediaId.split("-phantom-clone-")[0] + "-" + uniqueSuffix : uniqueSuffix;
          clone.setAttribute("data-media-id", newMediaId);
          clone.setAttribute("data-phantom-clone", "1");
          clone.setAttribute("data-media-position", (i + 1).toString());
          
          // Clean up active states from clone
          clone.classList.remove("is-active");
          
          mediaList.appendChild(clone);
          hasAppended = true;
        }
      }
      
      if (thumbList && thumbs.length > 0 && thumbs.length < neededCount) {
        // Re-query slides after appending to get the newly appended slides with their correct IDs
        var slidesAfter = mediaList ? mediaList.querySelectorAll("li.product__media-item, li.slider__slide, .product__media-item, .product-gallery__slide") : [];
        for (var j = thumbs.length; j < neededCount; j++) {
          var lastThumb = thumbs[thumbs.length - 1];
          var cloneThumb = lastThumb.cloneNode(true);
          
          var uniqueSuffix = "phantom-clone-" + j;
          if (cloneThumb.id) {
            cloneThumb.id = cloneThumb.id.split("-phantom-clone-")[0] + "-" + uniqueSuffix;
          }
          cloneThumb.setAttribute("data-phantom-clone", "1");
          cloneThumb.setAttribute("data-media-position", (j + 1).toString());
          
          // Set correct target pointing to the new cloned slide's media-id!
          if (slidesAfter[j]) {
            var targetMediaId = slidesAfter[j].getAttribute("data-media-id");
            if (targetMediaId) {
              cloneThumb.setAttribute("data-target", targetMediaId);
            }
          }
          
          // Clean up active classes/attributes on thumbnail button
          var btn = cloneThumb.querySelector("button");
          if (btn) {
            btn.classList.remove("is-active");
            btn.removeAttribute("aria-current");
            if (slidesAfter[j]) {
              btn.setAttribute("aria-controls", slidesAfter[j].id);
            }
          }
          
          thumbList.appendChild(cloneThumb);
          hasAppended = true;
        }
      }

      var totalCounters = document.querySelectorAll(".slider-counter--total");
      for (var c = 0; c < totalCounters.length; c++) {
        totalCounters[c].textContent = neededCount;
      }

      if (hasAppended) {
        // Dawn's slider-component custom element calculates child slides. Instead of cloning
        // the whole component (which breaks lifecycle events and leaves unstyled arrows on iOS WebKit),
        // or leaving it static (which fails to register the new slides and leaves them collapsed/hidden),
        // we create a fresh, clean custom element, copy all attributes, move all original children over,
        // and replace the old slider in the DOM. This natively triggers connectedCallback and event binding in WebKit!
        var sliders = document.querySelectorAll("slider-component");
        for (var s = 0; s < sliders.length; s++) {
          var slider = sliders[s];
          try {
            var newSlider = document.createElement("slider-component");
            for (var a = 0; a < slider.attributes.length; a++) {
              var attr = slider.attributes[a];
              newSlider.setAttribute(attr.name, attr.value);
            }
            while (slider.firstChild) {
              newSlider.appendChild(slider.firstChild);
            }
            slider.parentNode.replaceChild(newSlider, slider);
          } catch (err) {
            // Fallback to resize event if replacement fails
            try { window.dispatchEvent(new Event("resize")); } catch (_) {}
          }
        }
        try { window.dispatchEvent(new Event("resize")); } catch (_) {}
      }
    } catch (e) {
      try { console.error("[phantom-swap] ensureGallerySlots failed", e); } catch (_) {}
    }
  }

  // Some swap selectors (THUMB_SEL's `form[action*='/cart/add'] img`,
  // `.product-form img`, `.bundle-*`) are broad enough to also match the
  // Catching Bundle carousel, which the theme inserts INSIDE the product
  // form and which owns its own per-product branded images. Those nodes are
  // tagged `data-phantom-skip` (and live under `.phantom-cb-root`); never
  // overwrite them with the MAIN product's gallery images.
  function isOwnWidgetImg(img) {
    try { return !!(img.closest && img.closest("[data-phantom-skip],.phantom-cb-root")); }
    catch (_e) { return false; }
  }

  function applyImagesToGallery() {
    if (!activeProductImages || !activeProductImages.length) return;

    // Dynamically ensure we have enough slots in the DOM to show all branded images!
    ensureGallerySlots(activeProductImages.length);

    var imgs = document.querySelectorAll(IMG_SEL);
    for (var i = 0; i < imgs.length; i++) {
      if (isOwnWidgetImg(imgs[i])) continue;
      // Cycle through branded images so additional gallery slots never
      // fall back to a clean image — slot N takes images[N % images.length].
      var b = activeProductImages[i % activeProductImages.length];
      if (b && b.src) swapImg(imgs[i], b.src, b.alt);
    }
    var thumbs = document.querySelectorAll(THUMB_SEL);
    for (var j = 0; j < thumbs.length; j++) {
      if (isOwnWidgetImg(thumbs[j])) continue;
      var t = activeProductImages[j % activeProductImages.length];
      if (t && t.src) swapImg(thumbs[j], t.src, t.alt);
    }
    // Also swap the sticky add to cart image!
    try {
      var stickyImgs = document.querySelectorAll(".sticky-atc__image img");
      for (var k = 0; k < stickyImgs.length; k++) {
        var sImg = activeProductImages[0];
        if (sImg && sImg.src) swapImg(stickyImgs[k], sImg.src, sImg.alt);
      }
    } catch (_e) {}
  }

  // PDP gallery anti-flicker. The Horizon <slider-component> re-init (triggered
  // by ensureGallerySlots' slide cloning + element replace) and the theme lazy
  // loader asynchronously RESET our branded img src for a few hundred ms after
  // the swap, so the gallery visibly flickers clean->branded on the product page
  // (the "só na página dos produtos" break). We hold ONLY the gallery images at
  // opacity:0 until their branded src is applied AND stable, then fade them in.
  // SCOPED to gallery <img> (never the body/layout) + a hard 2s failsafe, so it
  // can never white-screen or trap the page — worst case the images fade in 2s.
  var galleryHoldDone = false;
  function installGalleryHold() {
    if (!productMatch || galleryHoldDone) return;
    try {
      if (!document.getElementById("phantom-gallery-hold")) {
        var st = document.createElement("style");
        st.id = "phantom-gallery-hold";
        st.textContent =
          "html.phantom-gallery-pending .product__media-list img," +
          "html.phantom-gallery-pending .product__media img," +
          "html.phantom-gallery-pending .product__media-item img," +
          "html.phantom-gallery-pending .product-gallery img," +
          "html.phantom-gallery-pending .product-media img," +
          "html.phantom-gallery-pending [class*='product__media'] img" +
          "{opacity:0!important;transition:opacity .18s ease}";
        (document.head || document.documentElement).appendChild(st);
      }
      document.documentElement.classList.add("phantom-gallery-pending");
      function reveal() {
        if (galleryHoldDone) return;
        galleryHoldDone = true;
        try { strip(document.documentElement, "phantom-gallery-pending"); } catch (_e) {}
      }
      var stable = 0, ticks = 0;
      var iv = setInterval(function () {
        ticks++;
        var imgs = document.querySelectorAll(IMG_SEL), pending = false, seen = 0;
        for (var i = 0; i < imgs.length; i++) {
          if (isOwnWidgetImg(imgs[i])) continue;
          seen++;
          var want = imgs[i].getAttribute("data-phantom-src");
          if (!want || (imgs[i].src || "").indexOf(want) === -1) { pending = true; break; }
        }
        if (seen > 0 && !pending) stable++; else stable = 0;
        // Reveal after ~2 consecutive stable ticks (slider settled) or 2s cap.
        if (stable >= 2 || ticks >= 25) { clearInterval(iv); reveal(); }
      }, 80);
      setTimeout(reveal, 2000);
    } catch (_e) {}
  }

  function applyProduct(real) {
    if (!real) return;
    // Swap EVERY title-like element so Dawn's duplicated <h1>+<h2> render
    // (the linked share-title inside the wrapper div) both reflect the
    // branded text.
    if (real.title) {
      var titles = document.querySelectorAll(TITLE_SEL);
      for (var i = 0; i < titles.length; i++) {
        var el = titles[i];
        var tag = (el.tagName || "").toLowerCase();
        // Allow common text tags, and only skip containers (like div) if they have child elements (to protect Dawn's wrapper)
        if (tag !== "h1" && tag !== "h2" && tag !== "h3" && tag !== "span" && tag !== "p" && tag !== "a") {
          if (el.children && el.children.length > 0) continue;
        }
        el.textContent = real.title;
      }
      // Also swap the sticky add to cart title
      try {
        var stickyTitles = document.querySelectorAll(".sticky-atc__title");
        for (var s = 0; s < stickyTitles.length; s++) {
          stickyTitles[s].textContent = real.title;
        }
      } catch (_e) {}
    }

    var d = document.querySelector(DESC_SEL);
    if (d && real.descriptionHtml != null) d.innerHTML = real.descriptionHtml;

    if (real.images && real.images.length) {
      activeProductImages = real.images;
      applyImagesToGallery();
    }
    var metas = [
      ['meta[property="og:title"]', "content", real.title],
      ['meta[property="og:description"]', "content", real.descriptionHtml ? plain(real.descriptionHtml) : null],
      ['meta[name="twitter:title"]', "content", real.title],
      ['meta[name="twitter:description"]', "content", real.descriptionHtml ? plain(real.descriptionHtml) : null]
    ];
    for (var k = 0; k < metas.length; k++) {
      if (!metas[k][2]) continue;
      var el = document.querySelector(metas[k][0]);
      if (el) el.setAttribute(metas[k][1], metas[k][2]);
    }
    if (real.title) { try { document.title = real.title; } catch (_e) {} }
    
    MAIN_PRODUCT_REAL = real;
    applyBundles();
    relocateStickyAtc();
  }

  var MAIN_PRODUCT_REAL = null;

  // NOTE: the "Catching Bundle" recommendation carousel (.phantom-cb-card) is
  // branded NATIVELY by the theme-layout widget (it overlays BRAND_CACHE title +
  // image onto each pick at build time). phantom-swap.js must NOT touch those
  // cards — they are tagged data-phantom-skip and the widget cycles through the
  // product's branded images, so setting data-phantom-src here would make the
  // src-guard fight that cycling. Leave them to the widget.
  function applyBundles() {
    if (!MAIN_PRODUCT_REAL) return;
    try {
      var bundles = document.querySelectorAll(".kaching-bundles, .kb-bundle, .kaching-bundle-container, [data-bundle-id], .rct_bundle_container, .bndl-wrapper");
      for (var w = 0; w < bundles.length; w++) {
        var imgs = bundles[w].querySelectorAll("img");
        var idx = 0;
        for (var i = 0; i < imgs.length; i++) {
          // ignore tracking pixels or tiny icons
          if (imgs[i].width > 0 && imgs[i].width < 30) continue;
          var b = MAIN_PRODUCT_REAL.images[idx % MAIN_PRODUCT_REAL.images.length];
          if (b && b.src) swapImg(imgs[i], b.src, b.alt);
          idx++;
        }
        var titles = bundles[w].querySelectorAll(".kb-bundle-title, .kaching-bundle-title, .bundle-title, .product-title, h3");
        for (var t = 0; t < titles.length; t++) {
          var inner = titles[t].querySelector("span") || titles[t];
          if (inner && MAIN_PRODUCT_REAL.title) inner.textContent = MAIN_PRODUCT_REAL.title;
        }
      }
    } catch (_e) {}
  }

  var lastCartFetchTime = 0;
  var isCartFetching = false;
  function applyCartDrawerDiscount(forceFetch) {
    try {
      var priceEls = document.querySelectorAll(
        ".cart-drawer__totals__row__money, .totals__subtotal-value, .totals__total-value, [data-cart-subtotal]"
      );
      if (priceEls.length === 0) return;

      var needUpdate = false;
      for (var p = 0; p < priceEls.length; p++) {
        var el = priceEls[p];
        if (!el.getAttribute("data-phantom-orig-price") || el.innerHTML.indexOf("phantom-orig-price") === -1) {
          needUpdate = true;
          break;
        }
      }
      if (!needUpdate && !forceFetch) return;

      var now = Date.now();
      if (isCartFetching || (now - lastCartFetchTime < 500 && !forceFetch)) return;
      isCartFetching = true;
      lastCartFetchTime = now;

      fetch("/cart.js")
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (cart) {
          isCartFetching = false;
          if (!cart) return;
          var qty = cart.item_count || 0;
          var discountPercent = 0;
          if (qty >= 5) {
            discountPercent = 32;
          } else if (qty === 4) {
            discountPercent = 25;
          } else if (qty === 3) {
            discountPercent = 18;
          } else if (qty === 2) {
            discountPercent = 10;
          }

          var origLabels = document.querySelectorAll(".phantom-drawer-discount-added");
          for (var l = 0; l < origLabels.length; l++) {
            origLabels[l].parentNode.removeChild(origLabels[l]);
          }

          if (discountPercent <= 0) {
            for (var p = 0; p < priceEls.length; p++) {
              var el = priceEls[p];
              if (el.getAttribute("data-phantom-orig-price")) {
                el.innerHTML = el.getAttribute("data-phantom-orig-price");
                el.removeAttribute("data-phantom-orig-price");
              }
            }
            return;
          }

          // BASE on items_subtotal_price (full price BEFORE the cart-level bundle
          // code), NEVER cart.total_price. The Catching-Bundle widget already
          // applied ONE PHANTOM_BUNDLE_* code via /cart/update.js, and Shopify
          // reflects it in cart.total_price — so re-applying the percent on
          // total_price double-discounted the drawer (18% showed as ~33%) and
          // diverged from checkout. items_subtotal_price is unaffected by the
          // cart-level code, so full - round(full*pct) == cart.total_price == the
          // exact amount checkout charges. Single discount, drawer === checkout.
          var originalTotal = cart.items_subtotal_price;
          var discountAmount = Math.round(originalTotal * (discountPercent / 100));
          var newTotal = originalTotal - discountAmount;

          var currency = cart.currency || "EUR";
          function formatMoney(cents) {
            var val = (cents / 100).toFixed(2);
            if (currency === "EUR" || location.hostname.indexOf(".de") > -1) {
              val = val.replace(".", ",") + " €";
            } else {
              val = currency === "USD" ? "$" + val : val + " " + currency;
            }
            return val;
          }

          var formattedOrig = formatMoney(originalTotal);
          var formattedNew = formatMoney(newTotal);

          priceEls = document.querySelectorAll(
            ".cart-drawer__totals__row__money, .totals__subtotal-value, .totals__total-value, [data-cart-subtotal]"
          );

          for (var p = 0; p < priceEls.length; p++) {
            var el = priceEls[p];
            if (el.getAttribute("data-phantom-orig-price") && el.innerHTML.indexOf(formattedNew) > -1) {
              continue;
            }
            // Strikethrough = the full subtotal (formattedOrig), NOT el.innerText —
            // if the theme ever renders the already-discounted total in this row,
            // reading it back would shrink the strikethrough every pass. The full
            // price is the stable, correct "before" value.
            var cleanText = el.getAttribute("data-phantom-orig-price") || formattedOrig;
            el.setAttribute("data-phantom-orig-price", cleanText);

            el.innerHTML = '<span style="text-decoration: line-through; opacity: 0.6; font-size: 0.85em; margin-right: 8px;">' + cleanText + '</span>' +
                           '<span style="color: #2bb673; font-weight: bold;">' + formattedNew + '</span>';
          }

          var footer = document.querySelector(".cart-drawer__totals, .totals, .cart-drawer__footer, .cart__blocks");
          if (footer && !document.querySelector(".phantom-drawer-discount-added")) {
            var indicator = document.createElement("div");
            indicator.className = "phantom-drawer-discount-added";
            indicator.style.cssText = "color: #2bb673; font-size: 13px; font-weight: bold; margin-top: 6px; text-align: right;";
            indicator.innerHTML = 'Catching Bundle (' + discountPercent + '% Rabatt angewendet!)';
            footer.appendChild(indicator);
          } else {
            var existingInd = document.querySelector(".phantom-drawer-discount-added");
            if (existingInd) {
              existingInd.innerHTML = 'Catching Bundle (' + discountPercent + '% Rabatt angewendet!)';
            }
          }
        })
        .catch(function () {
          isCartFetching = false;
        });
    } catch (_e) {
      isCartFetching = false;
    }
  }

  function relocateStickyAtc() {
    try {
      if (window.innerWidth >= 750) {
        var stickyAtc = document.querySelector("sticky-atc, .sticky-atc");
        if (stickyAtc && stickyAtc.parentNode !== document.body) {
          document.body.appendChild(stickyAtc);
        }
      }
    } catch (_e) {}
    // Localize the sticky button label — the theme hardcodes the English
    // "Add to cart" in the sticky bar while the main CTA is German
    // ("In den Warenkorb legen"). Match the store locale on the ad path.
    try {
      var labels = document.querySelectorAll(".sticky-atc__label__text, .sticky-atc__label");
      for (var i = 0; i < labels.length; i++) {
        var el = labels[i];
        // Only touch the leaf text node holder, and only if it's the EN string.
        if (el.querySelector && el.querySelector(".sticky-atc__label__text")) continue;
        var txt = (el.textContent || "").trim().toLowerCase();
        if (txt === "add to cart" || txt === "add to bag") {
          el.textContent = "In den Warenkorb legen";
        }
      }
    } catch (_e) {}
  }

  // Localize the theme sale badge. The imported catalog ships the discount badge
  // in SPANISH ("¡50% OFF!") through .price__badge-sale, which looks broken on a
  // German store. Rewrite it to a clean locale-neutral "-NN%" (keeping the badge's
  // own discount number). Only touches badges that still carry the ¡/OFF text, so
  // it's idempotent and never rewrites an already-localized badge.
  function localizeSaleBadges() {
    try {
      var badges = document.querySelectorAll(".price__badge-sale, .badge.price__badge-sale, .price__badge-sale .nowrap");
      for (var i = 0; i < badges.length; i++) {
        var b = badges[i];
        var t = b.textContent || "";
        if (!/¡|off/i.test(t)) continue;
        var m = t.match(/(\d{1,3})\s*%/);
        var nt = m ? "-" + m[1] + "%" : "Sale";
        var inner = b.querySelector(".nowrap") || b;
        if ((inner.textContent || "").replace(/\s+/g, "") !== nt) inner.textContent = nt;
      }
    } catch (_e) {}
  }

  // ---------- collection / cart / list swap ----------
  // Match product cards by extracting their /products/<handle> link. Each
  // unique handle yields one (handle, container) pair. Cart line items are
  // included so the cart drawer / /cart page also show branded titles +
  // thumbnails on the ad path.
  var CARD_CONTAINER_SEL = [
    ".card-wrapper",
    ".product-card-wrapper",
    ".card",
    ".product-card",
    ".product-item",
    ".grid__item",
    // Mobile-only carousel containers (Shrine/Horizon/Dawn use these on
    // narrow viewports for the recommendations strip).
    ".swiper-slide",
    ".splide__slide",
    ".slider__slide",
    ".flickity-slider > *",
    ".scroller__item",
    // Cart line items (Dawn/Shrine/Horizon variations)
    ".cart-item",
    ".cart-line",
    ".cart-drawer-item",
    ".cart__row",
    ".line-item",
    "[data-cart-item]",
    "tr.cart-item",
    "li.cart-item",
    "li.cart-drawer-item",
    "article",
  ].join(",");

  // Splide-carousel detector. secondary.js ships <splide-component> /
  // <slideshow-component> (Splide.js) for the PDP recommendations / bundle /
  // trend carousels. In loop mode Splide runs an AUTOPLAY tween (the t_/u
  // function in secondary.js, t_(s.interval, e.go.bind(e,">"), …)) that, every
  // cycle, cloneNode()s its slides and calls Layout.resize(). Those fresh clone
  // <li class="splide__slide …"> carry a[href*="/products/"], so phantom's
  // startObserver fires scheduleRescan -> swapAllCards -> applyCard rewrites the
  // clone (title+img+inline styles); Splide then destroys+recreates the clone on
  // the next tick -> re-added -> rescan. On the heaviest PDPs (combo-de-3-1's
  // 6-perfume set, the prada paquete — longest titles => most layout per
  // rebrand) each rebrand crosses the frame budget and the tween + phantom
  // correction saturate the main thread into the intermittent FREEZE. Splide
  // clones the ALREADY-branded original slide, so the visible clone stays
  // branded even when phantom never touches it — we simply must NOT react to
  // (or rewrite) Splide's ephemeral clone churn.
  function isInThemeCarousel(node) {
    try {
      return !!(node && node.closest && node.closest(
        "splide-component,slideshow-component,.splide,.splide__track,.splide__list,.splide__slide"
      ));
    } catch (_e) { return false; }
  }

  function collectCardHandles() {
    var items = [];
    var anchors = document.querySelectorAll('a[href*="/products/"]');
    var pairs = []; // multiple cards may reuse the same handle (drawer + cart page)
    for (var i = 0; i < anchors.length; i++) {
      var a = anchors[i];
      // Skip explicitly opted out elements
      if (a.closest("[data-phantom-skip]")) continue;
      // Skip Splide carousel slides — Splide owns their clone lifecycle and
      // recreates them from the (already-branded) originals every autoplay tick.
      // Rewriting them is the engine of the secondary.js t_/u freeze. The
      // original (non-clone) card grids/lists are NOT inside a splide subtree, so
      // every real product card on every page is still collected and branded.
      if (isInThemeCarousel(a)) continue;
      var m = (a.getAttribute("href") || "").match(/\/products\/([^\/?#]+)/);
      if (!m) continue;
      var handle = m[1];
      var card = a.closest(CARD_CONTAINER_SEL) || a;
      pairs.push({ handle: handle, card: card });
    }

    // Specially scan for cart drawer / cart page items that might not have anchors inside them!
    try {
      var cartItems = document.querySelectorAll('.cart-item, .cart-drawer-item, .cart-line, tr.cart-item, li.cart-item, [data-cart-item]');

      for (var c = 0; c < cartItems.length; c++) {
        var ci = cartItems[c];
        if (ci.closest("[data-phantom-skip]")) continue;
        
        // Check if we already have it in pairs
        var alreadyInPairs = false;
        for (var pI = 0; pI < pairs.length; pI++) {
          if (pairs[pI].card === ci) {
            alreadyInPairs = true;
            break;
          }
        }
        if (alreadyInPairs) continue;
        
        // Find handle from classes (e.g. cart-item--product-combo-de-3-11)
        var handle = null;
        var classList = ci.className || "";
        var mClass = classList.match(/cart-item--product-([a-zA-Z0-9\-_]+)/);
        if (mClass) {
          handle = mClass[1];
        }
        
        // Fallback to main PDP product handle if on the product page
        if (!handle && typeof productMatch !== "undefined" && productMatch) {
          handle = productMatch[1];
        }
        
        if (handle) {
          pairs.push({ handle: handle, card: ci });
        }
      }
    } catch (_e) {
      console.log("[phantom] collectCardHandles error:", _e);
    }

    // De-dup by card identity (not handle) so we re-apply on every card
    // even when multiple cards share a handle (e.g. line item + drawer +
    // mini-cart all referencing the same product).
    var seenCards = (typeof WeakSet !== "undefined") ? new WeakSet() : null;
    for (var k = 0; k < pairs.length; k++) {
      var p = pairs[k];
      if (seenCards && seenCards.has(p.card)) continue;
      if (seenCards) seenCards.add(p.card);
      items.push(p);
    }
    return items;
  }

  function applyCard(card, real, handle, variantIndex) {
    if (!card || !real) return;
    // Title — swap EVERY heading-like element in the card, not just the
    // first. Dawn renders the title twice per card (a media-overlay copy
    // AND a no-media text copy `h3.card__heading.h5`); a single querySelector
    // swapped only the media copy and left the no-media name showing the
    // clean text — that was the un-camouflaged name on collection grids.
    // Walk into <a>/<span> children so we replace the visible text without
    // nuking the link wrapper. Cart line items use `.cart-item__name`,
    // `.line-item__title`, `.cart-drawer-item__name`, etc — included below.
    if (real.title) {
      var titleEls = card.querySelectorAll(
        ".card__heading," +
        ".card-title," +
        ".product-card__title," +
        ".product-item__title," +
        ".card-information__title," +
        ".cart-item__name," +
        ".cart-item__title," +
        ".cart-drawer-item__name," +
        ".cart-drawer-item__title," +
        ".line-item__title," +
        ".cart-line__title",
      );
      if (!titleEls.length) {
        // No structured heading — fall back to the first generic heading or
        // the product link itself.
        var fallbackTitle = card.querySelector("h2,h3,h4,a[href*='/products/']");
        titleEls = fallbackTitle ? [fallbackTitle] : [];
      }
      for (var ti = 0; ti < titleEls.length; ti++) {
        var inner = titleEls[ti].querySelector("a,span") || titleEls[ti];
        inner.textContent = real.title;
      }
    }
    // Swap card images. To keep mobile data usage sane (each branded
    // image is ~400KB and a collection grid has ~30 cards * ~4 imgs each
    // = ~50MB extra payload otherwise), we only swap the FIRST image
    // per card by default and tag the rest as `loading="lazy"` so the
    // browser defers them until viewport entry.
    if (real.images && real.images.length) {
      var targetImageIndex = typeof variantIndex === "number" ? variantIndex : 0;
      var firstBranded = real.images[targetImageIndex % real.images.length];
      var cardImgs = card.querySelectorAll("img");
      for (var i = 0; i < cardImgs.length; i++) {
        // Never touch images owned by the Catching Bundle carousel. On the
        // PDP its cards live inside the product container, so a main-product
        // card whose closest() resolves to a wrapping `article`/`.grid__item`
        // would otherwise have THIS blind querySelectorAll("img") overwrite
        // every bundle card with the main product's images (cycling by index)
        // — that was the "all bundle items show the main image" bug. The
        // widget owns its own per-product branded images.
        if (isOwnWidgetImg(cardImgs[i])) continue;
        if (i === 0) {
          // Primary image — swap eagerly so it paints with the page.
          if (firstBranded && firstBranded.src) {
            swapImg(cardImgs[i], firstBranded.src, firstBranded.alt);
          }
        } else {
          // Secondary images (hover/back) — let the browser lazy-load
          // them. We still swap the src so when they DO load they're
          // branded, but they don't block the first paint.
          var b = real.images[i % real.images.length];
          if (b && b.src) {
            try { cardImgs[i].loading = "lazy"; } catch (_e) {}
            swapImg(cardImgs[i], b.src, b.alt);
          }
        }
      }
      // <source srcset="..."> inside <picture> — only swap the first
      // source for the same bandwidth reason.
      var sources = card.querySelectorAll("picture source");
      if (sources.length && firstBranded && firstBranded.src) {
        try {
          sources[0].srcset = firstBranded.src;
          if (sources[0].removeAttribute) sources[0].removeAttribute("data-srcset");
        } catch (_e) {}
      }
    }
  }

  // ---------- header logo swap (runs on every page when token is active) ----------
  // Direct <img> hits — fastest path, swap src in place.
  var LOGO_IMG_SEL = [
    ".header__heading-logo",
    ".header__heading-link img",
    ".header__logo img",
    ".site-header__logo img",
    ".site-header__logo-image",
    ".logo img",
    ".logo-image",
    "header a[href='/'] img",
    "[data-header-logo] img",
    ".header img.logo",
  ].join(",");

  // Container fallbacks — themes that render the brand name as text
  // (no <img>) get an <img> injected inside the heading link instead.
  var LOGO_CONTAINER_SEL = [
    ".header__heading-link",
    ".header__heading a[href='/']",
    "h1.header__heading a",
    ".site-header__logo a",
    ".header__logo a",
  ].join(",");

  function injectLogoIntoContainer(container, src, alt) {
    // Replace text/spans with a single <img>.
    try {
      // Clear children but keep the link intact.
      while (container.firstChild) container.removeChild(container.firstChild);
      var img = document.createElement("img");
      img.src = src;
      img.alt = alt || "";
      img.className = "phantom-header-logo";
      // Responsive sizing — clamp based on viewport so mobile gets a tight
      // logo and desktop shows it large. clamp(minVw, scalingVw, maxDesktop).
      img.style.height = "auto";
      img.style.width = "auto";
      img.style.maxWidth = "100%";
      img.style.maxHeight = "clamp(80px, 20vw, 250px)";
      img.style.objectFit = "contain";
      img.style.display = "inline-block";
      img.setAttribute("decoding", "async");
      img.setAttribute("fetchpriority", "high");
      container.appendChild(img);
      // Some themes constrain the parent heading height — relax it.
      try {
        var parentH = container.closest(".header__heading,h1,h2");
        if (parentH) {
          parentH.style.maxHeight = "none";
          parentH.style.height = "auto";
        }
      } catch (_e) {}
    } catch (_e) {}
  }
  
  // Extra CSS fix for inline injected logos (e.g. via theme-injector) + desktop sticky ATC premium style overrides + mobile gallery buttons fix
  try {
    var style = document.createElement("style");
    style.id = "phantom-branding-extra-styles";
    style.innerHTML = ".header__heading-logo, .header__heading-link img, .header__logo img, .site-header__logo img, .site-header__logo-image, .logo img, .logo-image, header a[href=\"/\"] img, [data-header-logo] img, .header img.logo { max-height: 120px !important; max-width: 85vw !important; width: auto !important; height: auto !important; object-fit: contain !important; } @media (min-width: 768px) { .header__heading-logo, .header__heading-link img, .header__logo img, .site-header__logo img, .site-header__logo-image, .logo img, .logo-image, header a[href=\"/\"] img, [data-header-logo] img, .header img.logo { max-height: 50px !important; max-width: 220px !important; } } @media (max-width: 749px) { body { padding-bottom: 90px !important; } .product__media img, .product__media-item img, .product-gallery img, .product-gallery__slide img, slider-component img { object-fit: contain !important; padding: 0 !important; box-sizing: border-box !important; } .slider-buttons, .slider-button, slider-component .slider-buttons, .slider-counter, .slider-counter--total, .slider-buttons__container { display: none !important; } } @media (max-width: 989px) { .product__column-sticky, .product__media-wrapper, .product-single__media-group, [class*=\"column-sticky\"], [class*=\"media-wrapper\"] { position: relative !important; top: auto !important; height: auto !important; overflow: visible !important; } } @media (min-width: 750px) { .sticky-atc { position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; margin: 0 !important; display: block !important; z-index: 999 !important; background: #ffffff !important; border-top: 1px solid rgba(0, 0, 0, 0.08) !important; box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.05) !important; padding: 1rem 0 !important; transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1) !important; } .sticky-atc-container { max-width: 1200px !important; margin: 0 auto !important; padding: 0 4rem !important; display: flex !important; align-items: center !important; gap: 1.5rem !important; justify-content: space-between !important; } .sticky-atc__image { flex: 0 0 auto !important; width: 56px !important; height: 56px !important; border-radius: 6px !important; overflow: hidden !important; box-shadow: 0 2px 8px rgba(0,0,0,0.06) !important; } .sticky-atc__image img { width: 100% !important; height: 100% !important; object-fit: cover !important; border-radius: 6px !important; transition: transform 0.2s ease !important; } .sticky-atc__image img:hover { transform: scale(1.05) !important; } .sticky-atc__left { flex: 1 1 auto !important; min-width: 0 !important; } .sticky-atc__button { flex: 0 0 auto !important; } .sticky-atc__button button { background: #000 !important; color: #fff !important; border: none !important; border-radius: 50px !important; font-weight: 600 !important; font-size: 1.4rem !important; letter-spacing: 0.05em !important; text-transform: uppercase !important; padding: 1.2rem 3rem !important; min-width: 220px !important; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15) !important; transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important; } .sticky-atc__button button:hover { background: #222 !important; box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25) !important; transform: translateY(-2px) !important; } .sticky-atc__title { font-weight: 600 !important; font-size: 1.5rem !important; color: #111 !important; } } @media (max-width: 749px) { .sticky-atc { position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; margin: 0 !important; z-index: 999 !important; background: #ffffff !important; border-top: 1px solid rgba(0,0,0,0.08) !important; box-shadow: 0 -6px 20px rgba(0,0,0,0.07) !important; padding: 8px 0 !important; } .sticky-atc-container { display: flex !important; align-items: center !important; gap: 10px !important; max-width: 100% !important; padding: 0 12px !important; } .sticky-atc__left { flex: 1 1 auto !important; min-width: 0 !important; } .sticky-atc__left__content { min-width: 0 !important; } .sticky-atc__title { font-size: 12px !important; line-height: 1.25 !important; font-weight: 600 !important; color: #111 !important; display: -webkit-box !important; -webkit-line-clamp: 2 !important; -webkit-box-orient: vertical !important; overflow: hidden !important; margin: 0 0 2px 0 !important; } .sticky-atc__price, .sticky-atc__price .price, .sticky-atc__price .price-item { font-size: 13px !important; line-height: 1.2 !important; } .sticky-atc__button { flex: 0 0 auto !important; } .sticky-atc__button button { font-size: 12px !important; font-weight: 700 !important; letter-spacing: 0.02em !important; text-transform: uppercase !important; padding: 11px 14px !important; min-width: 0 !important; width: auto !important; white-space: nowrap !important; line-height: 1.1 !important; border-radius: 40px !important; background: #000 !important; color: #fff !important; border: none !important; } }";
    document.head.appendChild(style);
  } catch(_e) {}

  function injectAdBanner(src, alt, mobileSrc) {
    if (!src) return;
    try {
      if (document.getElementById("phantom-ad-banner")) return; // idempotent
      var wrapper = document.createElement("div");
      wrapper.id = "phantom-ad-banner";
      wrapper.style.cssText = [
        "position:relative",
        "width:100%",
        "margin:0",
        "padding:0",
        "background:#000",
        "display:block",
        "line-height:0",
        "z-index:1",
      ].join(";");
      // Use <picture> when a mobile-specific source is provided so the
      // browser picks the right asset natively — no flicker, no JS resize
      // listener needed.
      var imgStyle = [
        "display:block",
        "width:100%",
        "height:auto",
        "max-width:100%",
        "object-fit:cover",
        "object-position:center",
      ].join(";");
      var img;
      if (mobileSrc) {
        var picture = document.createElement("picture");
        var source = document.createElement("source");
        source.media = "(max-width: 768px)";
        source.srcset = mobileSrc;
        picture.appendChild(source);
        img = document.createElement("img");
        img.src = src;
        img.alt = alt || "";
        img.style.cssText = imgStyle;
        picture.appendChild(img);
        wrapper.appendChild(picture);
      } else {
        img = document.createElement("img");
        img.src = src;
        img.alt = alt || "";
        img.style.cssText = imgStyle;
        wrapper.appendChild(img);
      }

      // Insert INSIDE main content on the homepage — right at the top
      // so it acts as a hero. This prevents white space between header and main.
      var anchor = document.querySelector("main, #MainContent, .main-content");
      if (anchor) {
        anchor.insertBefore(wrapper, anchor.firstChild);
      } else if (document.body) {
        var hdr = document.querySelector("header, .site-header, .shopify-section-header");
        if (hdr && hdr.parentNode) {
          if (hdr.nextSibling) hdr.parentNode.insertBefore(wrapper, hdr.nextSibling);
          else hdr.parentNode.appendChild(wrapper);
        } else {
          document.body.insertBefore(wrapper, document.body.firstChild);
        }
      }
      
      // Additional CSS to force zero margins around the injected banner
      try {
        var fixStyle = document.createElement("style");
        fixStyle.innerHTML = "main, #MainContent, .main-content { margin-top: 0 !important; padding-top: 0 !important; } #phantom-ad-banner { display: flex; flex-direction: column; }";
        document.head.appendChild(fixStyle);
      } catch(_e) {}
    } catch (_e) {}
  }

  // Hung fetches were the root cause of "card swap never finishes" on
  // weak mobile networks: without an explicit timeout iOS Safari can keep
  // a connection pending for 60-300s, so the 1s polling fallback below
  // can't retry because the previous request never settles. We abort at
  // 8s and let the polling loop fire a fresh attempt.
  function fetchWithTimeout(url, opts, ms) {
    opts = opts || {};
    ms = ms || 8000;
    if (typeof AbortController === "function") {
      try {
        var ctl = new AbortController();
        var t = setTimeout(function () { try { ctl.abort(); } catch (_e) {} }, ms);
        opts.signal = ctl.signal;
        return fetch(url, opts).then(
          function (r) { clearTimeout(t); return r; },
          function (e) { clearTimeout(t); throw e; }
        );
      } catch (_e) { /* fall through */ }
    }
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error("timeout"));
      }, ms);
      fetch(url, opts).then(
        function (r) { if (settled) return; settled = true; clearTimeout(timer); resolve(r); },
        function (e) { if (settled) return; settled = true; clearTimeout(timer); reject(e); }
      );
    });
  }

  function swapHeaderLogo() {
    // Cache-bust per pageload so the user never gets stuck on a stale
    // branding response (the endpoint sets max-age=300 for edge caching,
    // but if we changed the logo behind the scenes the user's browser
    // would otherwise keep painting the old one for up to 5 minutes).
    var url =
      API +
      "/api/phantom/branding?shop=" +
      encodeURIComponent(SHOP) +
      "&_=" +
      Date.now();
    return fetchWithTimeout(url, { method: "GET", credentials: "omit", cache: "no-store", headers: { "Accept": "application/json" } }, 8000)
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.ok) return;

        // Logo swap. We can't use the generic `swapImg` here because it
        // forces width/height to 100% with `object-fit: cover` — that's
        // fine for product cards (square slots) but distorts wide
        // wordmark logos like OUTLET CITY METZINGEN (450×69). For the
        // header logo we preserve the natural aspect ratio with
        // `object-fit: contain` and let the height fill the slot.
        // Só troca o logo do header quando a LOJA tem branding configurado
        // (data.logo vindo dos metafields phantom.brand_logo da própria loja).
        // Sem isso NÃO tocamos no logo — a loja mantém o seu. (Antes havia um
        // `|| true` com fallback cravado "Outlet City Metzingen" que vazava a
        // marca da Metzingen em QUALQUER loja com token.)
        if (data && data.logo && data.logo.src) {
          var src = data.logo.src;
          var alt = data.logo.alt || "";
          var imgs = document.querySelectorAll(LOGO_IMG_SEL);
          var imgFound = false;
          for (var i = 0; i < imgs.length; i++) {
            var el = imgs[i];
            if (el.tagName === "IMG") {
              swapLogoImg(el, src, alt);
              imgFound = true;
            } else {
              try {
                el.style.backgroundImage = "url(" + src + ")";
                el.style.backgroundSize = "contain";
                el.style.backgroundRepeat = "no-repeat";
                el.style.backgroundPosition = "center";
              } catch (_e) {}
            }
          }
          if (!imgFound) {
            var containers = document.querySelectorAll(LOGO_CONTAINER_SEL);
            for (var j = 0; j < containers.length; j++) {
              injectLogoIntoContainer(containers[j], src, alt);
            }
          }
        }

        // Ad banner — homepage only. Product / collection pages stay clean
        // so the gallery is the focal point. Mobile gets its own asset.
        if (data.banner && data.banner.src && isIndex) {
          // Hide the theme's slideshow / image-banner sections first so
          // the visitor doesn't see two stacked banners (the theme's
          // slideshow vs the one we're about to inject). We scope this
          // tightly to slideshow-shaped sections to avoid hiding
          // featured-collection or trust badges.
          try {
            var hideStyle = document.createElement("style");
            hideStyle.id = "phantom-ad-hide-slideshow";
            hideStyle.textContent = [
              ".shopify-section--slideshow",
              "section.shopify-section.slideshow-section",
              ".shopify-section [class*='slideshow__']",
              "section[id*='slideshow']",
              "section[id*='hero']",
              "section[class*='image-banner']",
              ".shopify-section--image-banner",
            ].join(",") + "{display:none!important}";
            document.head.appendChild(hideStyle);
          } catch (_e) {}
          injectAdBanner(
            data.banner.src,
            data.banner.alt || "",
            data.banner.mobileSrc,
          );
        }

        // Footer + acordeões do PDP data-driven por loja. Só aplica quando a loja
        // tem copy branded configurada (data.copy de /api/phantom/branding). Sem
        // copy → não mexe no footer do tema (nunca vaza marca de outra loja).
        if (data.copy) {
          __brandCopy = data.copy;
          try { swapFooterBrand(); } catch (_e) {}
          try { swapPdpAccordions(); } catch (_e) {}
        }
      })
      .catch(function () {});
  }

  // ---------- network ----------
  // The snapshot endpoint pre-bakes every product's branded payload into a
  // single JS file loaded BEFORE this script. If the data is already in
  // memory we return it synchronously — no network, no timeout, no failure
  // mode. The /verify-swap[-batch] fetches stay as a fallback for products
  // the snapshot didn't include (new SKU added after snapshot was cached).
  function snapshotItem(handle) {
    try {
      var snap = window.PHANTOM_DATA;
      if (snap && snap.ok && snap.items && snap.items[handle]) {
        return snap.items[handle];
      }
    } catch (_e) {}
    return null;
  }

  function fetchSingle(handle) {
    var fromSnap = snapshotItem(handle);
    // Use the snapshot directly ONLY when it carries the full description. The
    // light snapshot (assets/phantom-data.js) ships title+images+vendor for fast
    // cold loads but omits descriptionHtml — on the PDP we still want the full
    // branded description, so fall through to the network when it's missing.
    if (fromSnap && fromSnap.descriptionHtml != null) {
      return Promise.resolve({ ok: true, real: fromSnap });
    }
    var __ref = "";
    try { __ref = encodeURIComponent((document.referrer || "").slice(0, 300)); } catch (_e) {}
    var url = API +
      "/api/phantom/verify-swap" +
      "?t=" + encodeURIComponent(TOKEN) +
      "&shop=" + encodeURIComponent(SHOP) +
      "&handle=" + encodeURIComponent(handle) +
      (__ref ? "&ref=" + __ref : "");
    return fetchWithTimeout(url, { method: "GET", credentials: "omit", headers: { "Accept": "application/json" } }, 8000)
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function fetchBatch(handles) {
    // Resolve as much as possible from the snapshot. Only handles that
    // weren't in the snapshot fall through to the network call.
    var resolved = {};
    var missing = [];
    for (var i = 0; i < handles.length; i++) {
      var h = handles[i];
      var hit = snapshotItem(h);
      if (hit) resolved[h] = hit;
      else missing.push(h);
    }
    if (missing.length === 0) {
      return Promise.resolve({ ok: true, items: resolved });
    }
    var url = API + "/api/phantom/verify-swap-batch";
    return fetchWithTimeout(url, {
      method: "POST",
      credentials: "omit",
      headers: { "Accept": "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ token: TOKEN, shop: SHOP, handles: missing })
    }, 10000)
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (data) {
        if (data && data.ok && data.items) {
          for (var k in data.items) {
            if (Object.prototype.hasOwnProperty.call(data.items, k)) {
              resolved[k] = data.items[k];
            }
          }
        }
        return { ok: true, items: resolved };
      })
      .catch(function () {
        return { ok: true, items: resolved };
      });
  }

  // Track handles we've already swapped (and the cards) so MutationObserver
  // re-runs only process *new* cards instead of refetching every paint.
  var swappedHandles = {};
  // We deliberately do NOT skip already-swapped cards — themes lazy-load
  // images on intersection and may overwrite our src AFTER the swap. By
  // re-applying on every poll/observer tick we self-heal. `swappedHandles`
  // (data cache) prevents redundant network calls, applyCard work is cheap.
  function swapAllCards(excludeHandle) {
    var items = collectCardHandles();
    // Do NOT filter out the main handle. The cart drawer needs to be swapped
    // even for the product currently being viewed!
    // Instead, pre-warm the cache using MAIN_PRODUCT_REAL if available.
    if (excludeHandle && typeof MAIN_PRODUCT_REAL !== "undefined" && MAIN_PRODUCT_REAL) {
      swappedHandles[excludeHandle] = MAIN_PRODUCT_REAL;
    }
    if (items.length === 0) return Promise.resolve();

    var toFetch = [];
    var cached = [];
    for (var i = 0; i < items.length; i++) {
      var h = items[i].handle;
      if (h in swappedHandles) {
        if (swappedHandles[h]) cached.push({ item: items[i], real: swappedHandles[h] });
      } else {
        toFetch.push(items[i]);
      }
    }
    
    // Process cached items grouped by handle to apply variant indexing
    var cardsByHandle = {};
    for (var c = 0; c < items.length; c++) {
      var p = items[c];
      if (!cardsByHandle[p.handle]) cardsByHandle[p.handle] = [];
      cardsByHandle[p.handle].push(p.card);
    }
    
    for (var handle in cardsByHandle) {
      if (!Object.prototype.hasOwnProperty.call(cardsByHandle, handle)) continue;
      if (swappedHandles[handle]) {
        var cards = cardsByHandle[handle];
        for (var k = 0; k < cards.length; k++) {
          applyCard(cards[k], swappedHandles[handle], handle, k);
        }
      }
    }
    
    if (toFetch.length === 0) return Promise.resolve();

    var handles = toFetch.map(function (x) { return x.handle; });
    for (var i = 0; i < handles.length; i++) {
      swappedHandles[handles[i]] = null;
    }
    return fetchBatch(handles).then(function (data) {
      if (!data || !data.ok || !data.items) return;
      for (var j = 0; j < toFetch.length; j++) {
        var real = data.items[toFetch[j].handle];
        if (real) {
          swappedHandles[toFetch[j].handle] = real;
        }
      }
      // Re-process freshly fetched handles
      for (var handle in cardsByHandle) {
        if (!Object.prototype.hasOwnProperty.call(cardsByHandle, handle)) continue;
        if (data.items && data.items[handle]) {
          var cards = cardsByHandle[handle];
          for (var k = 0; k < cards.length; k++) {
            applyCard(cards[k], data.items[handle], handle, k);
          }
        }
      }
    });
  }

  // ---------- dynamic content observer ----------
  // Shopify themes lazy-load the "recommended products" section on the PDP,
  // load more on scroll in collections, and re-render search results on
  // every keystroke. Each of those produces new product cards that need to
  // be swapped after the initial pass. A debounced MutationObserver catches
  // all of it.
  var mutationTimer = null;
  var rescanHits = [];
  function scheduleRescan() {
    if (PHANTOM_HALT || mutationTimer) return;
    // Short debounce normally (snapshot-backed swap is synchronous, so AJAX grids
    // re-brand almost immediately). BUT a theme reveal/slider tween (secondary.js)
    // can re-render product nodes every animation frame; reacting at 60x/s on the
    // heaviest PDPs (e.g. the 6-perfume set) saturated the main thread and FROZE
    // the page. Runaway guard: if we've scheduled many rescans in the last 2s, the
    // page is in a mutation storm — back the cadence WAY off so phantom never fights
    // an animation into a freeze. Drops back to the fast path once the storm passes.
    var now = Date.now();
    rescanHits.push(now);
    while (rescanHits.length && now - rescanHits[0] > 2000) rescanHits.shift();
    var delay = rescanHits.length > 20 ? 2500 : 80;
    mutationTimer = setTimeout(function () {
      mutationTimer = null;
      var excl = productMatch ? productMatch[1] : null;
      // Gallery re-walk intentionally removed from the rescan path: it re-poked
      // the slider's offsetHeight/ResizeObserver reflow and fed the freeze loop.
      // scheduleRescan is for AJAX-injected product CARDS; the PDP gallery is
      // branded once on swap and self-healed by startSrcGuard.
      try { applyBundles(); } catch (_e) {}
      swapAllCards(excl).catch(function () {});
    }, delay);
  }
  // Attribute observer for swapped images — themes (Dawn/Horizon lazy
  // loaders) sometimes re-set src after our swap when the image enters the
  // viewport. We watch for that and re-apply the branded src.
  function startSrcGuard() {
    if (typeof MutationObserver !== "function") return;
    try {
      var guardWriting = false;
      var guardHits = [];
      var mo = new MutationObserver(function (records) {
        // Ignore the mutation batch we ourselves just produced — otherwise
        // every corrective t.src write is re-observed and re-corrected,
        // which (with the theme lazy-loader) is the src ping-pong.
        if (guardWriting || PHANTOM_HALT) { if (PHANTOM_HALT) { try { mo.disconnect(); } catch (_e) {} } return; }
        // Runaway guard: a theme image-cycling / slider tween (secondary.js) can
        // knock our branded src off every animation frame; correcting at 60x/s
        // saturated the main thread and FROZE the heaviest PDPs. If we've corrected
        // too many times in the last 2s the page is in an animation storm — stop
        // guarding entirely (the slider just cycles through slides we already
        // branded) so phantom can never fight an animation into a freeze.
        var gnow = Date.now();
        while (guardHits.length && gnow - guardHits[0] > 2000) guardHits.shift();
        if (guardHits.length > 80) { try { mo.disconnect(); } catch (_e) {} return; }
        var dirty = [];
        for (var i = 0; i < records.length; i++) {
          var t = records[i].target;
          if (!t || t.tagName !== "IMG") continue;
          // Never re-correct an <img> inside a Splide carousel. Splide rewrites
          // its clone slides' src/srcset every animation frame; correcting them
          // here is a per-frame write that re-arms Splide's Layout tween — the
          // src-guard half of the secondary.js t_/u freeze. The original card
          // images (outside any splide subtree) are still guarded normally.
          if (isInThemeCarousel(t)) continue;
          var want = t.getAttribute("data-phantom-src");
          if (!want) continue;
          var cur = t.getAttribute("src") || t.src || "";
          // Only act when the theme actually knocked us OFF the branded URL.
          if (cur.indexOf(want) === -1) dirty.push(t);
          else if (t.getAttribute("srcset")) dirty.push(t);
        }
        if (!dirty.length) return;
        for (var gh = 0; gh < dirty.length; gh++) guardHits.push(gnow);
        guardWriting = true;
        try {
          for (var k = 0; k < dirty.length; k++) {
            var el = dirty[k];
            var w = el.getAttribute("data-phantom-src");
            var c = el.getAttribute("src") || el.src || "";
            if (c.indexOf(w) === -1) el.src = w;
            if (el.getAttribute("srcset")) { try { el.removeAttribute("srcset"); } catch (_e) {} }
          }
        } catch (_e) {}
        // Release on the next microtask/frame so our own writes have flushed
        // through the observer queue and are dropped.
        try { setTimeout(function () { guardWriting = false; }, 0); } catch (_e) { guardWriting = false; }
      });
      mo.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ["src", "srcset"],
      });
    } catch (_e) {}
  }

  function startObserver() {
    if (typeof MutationObserver !== "function" || !document.body) return;
    try {
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            // Splide carousel clone churn — IGNORE. secondary.js's autoplay tween
            // (t_/u) clones its slides every cycle; each clone re-fires this
            // observer. Reacting (scheduleRescan -> swapAllCards rewrites the
            // clone -> Splide recreates it -> re-added) is the tight loop that
            // FREEZES the heaviest PDPs. Splide clones the already-branded
            // original, so skipping its churn loses no branding.
            if (isInThemeCarousel(n)) continue;
            // Product card / link added.
            if (n.querySelector && n.querySelector('a[href*="/products/"]')) {
              scheduleRescan();
              return;
            }
            if (n.tagName === "A" && /\/products\//.test(n.getAttribute("href") || "")) {
              scheduleRescan();
              return;
            }
            // New gallery slide / zoom image on the PDP.
            if (productMatch && (
              n.matches && (
                n.matches(".product__media,.product__media img,.product-gallery,.product-gallery img,.product__media-item")
              ) ||
              n.querySelector && n.querySelector(".product__media img,.product-gallery img,.product__media-item img")
            )) {
              scheduleRescan();
              return;
            }
            // Cart drawer / cart page opened or re-rendered (add to cart,
            // quantity change, line removed). Re-run so cart titles +
            // thumbnails reflect the branded version on the ad path.
            var nClass = n.className || "";
            var nTag = n.tagName || "";
            var nodeName = (nClass + " " + nTag).toLowerCase();
            if (
              /cart-?drawer|cart-?items|mini-?cart|cart-?notification|cart-?contents/i.test(nodeName) ||
              (n.querySelector && n.querySelector(
                ".cart-item,.cart-drawer-item,.cart-line,.line-item,[data-cart-item],cart-items,cart-drawer-items"
              ))
            ) {
              scheduleRescan();
              return;
            }
          }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (_e) {}
  }

  // ---------- fetch hijack ----------
  // The inline <script> we ship in theme.liquid replaces window.fetch
  // synchronously DURING HTML parsing — BEFORE the <product-recommendations>
  // custom element fires its connectedCallback. That inline patch:
  //   * Calls window.__phantom_rec_handler(input, init, origFetch) if defined
  //     (when this script has booted and registered itself).
  //   * Otherwise queues the request in window.__phantom_rec_q and returns
  //     a Promise that resolves later.
  // We expose the handler + drain the queue on boot.
  function buildRecHandler() {
    return function recHandler(input, init, origFetch) {
      var urlStr = "";
      try { urlStr = typeof input === "string" ? input : (input && input.url) || ""; } catch (_) {}
      if (urlStr && /\/cart/i.test(urlStr)) {
        setTimeout(function() { applyCartDrawerDiscount(true); }, 300);
        setTimeout(function() { applyCartDrawerDiscount(true); }, 800);
        setTimeout(function() { applyCartDrawerDiscount(true); }, 1500);
      }
      return origFetch(input, init).then(function (resp) {
        if (!resp || !resp.ok) return resp;
        return resp.clone().text().then(function (html) {
          var handleSet = {};
          var re = /\/products\/([a-z0-9\-]+)/gi;
          var m;
          while ((m = re.exec(html)) !== null) handleSet[m[1]] = 1;
          var handles = Object.keys(handleSet);
          if (handles.length === 0) return resp;
          var toFetch = handles.filter(function (h) { return !(h in swappedHandles); });
          var fetchPromise = toFetch.length === 0
            ? Promise.resolve(null)
            : (function() {
                for (var i = 0; i < toFetch.length; i++) {
                  swappedHandles[toFetch[i]] = null;
                }
                return fetchBatch(toFetch).then(function (data) {
                  if (data && data.ok && data.items) {
                    for (var hk in data.items) {
                      if (Object.prototype.hasOwnProperty.call(data.items, hk)) {
                        swappedHandles[hk] = data.items[hk];
                      }
                    }
                  }
                });
              })();
          return fetchPromise.then(function () {
            var rewritten = rewriteRecommendationsHTML(html, handles);
            try {
              return new Response(rewritten, {
                status: resp.status,
                statusText: resp.statusText,
                headers: resp.headers,
              });
            } catch (_e) {
              return resp;
            }
          });
        }).catch(function () { return resp; });
      });
    };
  }

  function installRecommendationsHijack() {
    var handler = buildRecHandler();
    // Register so new fetch calls from the prewarm patch go straight through us.
    try { window.__phantom_rec_handler = handler; } catch (_e) {}
    // Drain anything queued before we booted.
    try {
      var q = window.__phantom_rec_q;
      if (q && q.length) {
        q.splice(0, q.length).forEach(function (item) {
          handler(item.input, item.init, item.of).then(item.resolve, item.reject);
        });
      }
    } catch (_e) {}
    // Belt + suspenders: if the prewarm wasn't installed (older cached theme
    // version), also patch fetch here directly so we still catch later
    // calls within the same page. Pattern matches recommendations,
    // section_id-based section renders (collection/featured/search) and
    // cart endpoints — anything that might return HTML with product
    // anchors that need to be branded.
    var URL_RE = /\/recommendations\/products|[?&]section_id=|\/cart(?:\.js|\.json|\/)|\/search\?|\/collections\/[a-z0-9-]+\.(?:json|js)/i;
    if (!window.__phantom_rec_q && window.fetch) {
      var of = window.fetch.bind(window);
      window.fetch = function (input, init) {
        var url = "";
        try {
          url = typeof input === "string" ? input : (input && input.url) || "";
        } catch (_e) {}
        if (!URL_RE.test(url)) return of(input, init);
        return handler(input, init, of);
      };
    }
  }

  // Rewrites a chunk of Shopify recommendations HTML in-place using cached
  // branded data. Replaces img src/srcset and the <a> title text.
  function rewriteRecommendationsHTML(html, handles) {
    var out = html;
    handles.forEach(function (handle) {
      var real = swappedHandles[handle];
      if (!real) return;
      var firstImg = real.images && real.images[0] && real.images[0].src;
      if (firstImg) {
        // Replace every <img> tag that sits within the same <li>/<div>
        // wrapper as the anchor /products/<handle>. We scope by handle:
        // anchor.href contains "/products/<handle>", and the <img>
        // typically sits in the same parent. Easiest approach: find
        // each `card-wrapper` or `<li>` block containing the handle and
        // replace its <img src + srcset> attrs.
        var blockRe = new RegExp(
          "(<(?:li|div)[^>]*(?:card-wrapper|grid__item)[^>]*>[\\s\\S]*?\\/products\\/" +
            escapeForRegex(handle) +
            "[\\s\\S]*?<\\/(?:li|div)>)",
          "i",
        );
        var bm = out.match(blockRe);
        if (bm) {
          var block = bm[0];
          // Strip srcset and replace src in every <img> inside the block.
          var newBlock = block
            .replace(/(<img[^>]*?)\ssrcset="[^"]*"/gi, "$1")
            .replace(/(<img[^>]*?)\ssizes="[^"]*"/gi, "$1")
            .replace(/(<img[^>]*?)\sloading="lazy"/gi, '$1 loading="eager"')
            .replace(
              /<img([^>]*?)\ssrc="[^"]+"/i,
              '<img$1 src="' + firstImg + '" data-phantom-src="' + firstImg + '"',
            );
          // Also patch title text inside the anchor — find <a ...><...>TITLE</...></a>
          if (real.title) {
            newBlock = newBlock.replace(
              new RegExp(
                "(<a[^>]*\\/products\\/" + escapeForRegex(handle) + "[^>]*>)([\\s\\S]*?)(<\\/a>)",
                "i",
              ),
              function (_full, openA, inner, closeA) {
                // Replace only text nodes — keep nested spans intact by
                // collapsing inner to plain branded title.
                return openA + escapeHtml(real.title) + closeA;
              },
            );
          }
          out = out.replace(block, newBlock);
        }
      }
    });
    return out;
  }

  function escapeForRegex(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // ---------- dispatch ----------
  var logoTask = swapHeaderLogo();
  // Install the fetch hijack BEFORE any startup runs so the theme's
  // initial recommendations request is intercepted.
  installRecommendationsHijack();

  // Override the footer copy on the ad path: switch the generic theme footer
  // ("Über uns" / "Kontakt" blocks) for the branded copy of THIS store. The copy
  // is data-driven per store (__brandCopy.footer, vindo de /api/phantom/branding) —
  // não há mais texto cravado da Metzingen. Organic visitors never see this
  // because phantom-swap doesn't run for them (token gate).
  function swapFooterBrand() {
    if (!__brandCopy || !__brandCopy.footer) return;

    function applySwap() {
      if (PHANTOM_HALT) return; // circuit breaker — não brigar com storm de reflow
      if (!__brandCopy || !__brandCopy.footer) return;
      var about = __brandCopy.footer.about && __brandCopy.footer.about.html;
      var contact = __brandCopy.footer.contact && __brandCopy.footer.contact.html;
      var blocks = document.querySelectorAll(".footer-block");
      for (var i = 0; i < blocks.length; i++) {
        var block = blocks[i];
        var heading = block.querySelector(".footer-block__heading");
        var content = block.querySelector(".footer-block__details-content");
        if (!heading || !content) continue;
        var title = (heading.textContent || "").trim().toLowerCase();
        // "Über uns" block — replace with the long brand copy
        if (about && /(uber|über)\s*uns/.test(title)) {
          if (content.getAttribute('data-phantom-applied') !== 'about') {
            content.innerHTML = about;
            content.setAttribute('data-phantom-applied', 'about');
          }
        }
        // "Kontakt" block — replace with the store's branded address + email
        else if (contact && /kontakt/.test(title)) {
          if (content.getAttribute('data-phantom-applied') !== 'kontakt') {
            content.innerHTML = contact;
            content.setAttribute('data-phantom-applied', 'kontakt');
          }
        }
      }
    }
    applySwap();
    // Wire o observer/interval só uma vez (swapHeaderLogo pode rodar de novo no
    // resume) — applySwap acima já re-aplica imediatamente a copy mais recente.
    if (__footerWired) return;
    __footerWired = true;
    // Reapply on DOM changes — section reloads (drawer / cart updates)
    // can rerender the footer. Respeita o circuit breaker: se PHANTOM_HALT trip,
    // desconecta o observer (igual ao src-guard) pra não alimentar o freeze.
    if (typeof MutationObserver === "function") {
      try {
        var mo = new MutationObserver(function () {
          if (PHANTOM_HALT) { try { mo.disconnect(); } catch (_e) {} return; }
          applySwap();
        });
        mo.observe(document.body, { subtree: true, childList: true });
      } catch (_e) {}
    }
    // Belt-and-suspenders: re-run periodically (cheap, sub-1ms) — para quando HALT.
    var footerTimer = setInterval(function () {
      if (PHANTOM_HALT) { try { clearInterval(footerTimer); } catch (_e) {} return; }
      applySwap();
    }, 1500);
  }

  function swapPdpAccordions() {
    // Conteúdo dos acordeões data-driven por loja (__brandCopy.pdp). Sem copy
    // configurada → não injeta nada (não vaza texto/marca de outra loja).
    if (!__brandCopy || !__brandCopy.pdp) return;

    // Metadata por chave, lida AO VIVO de __brandCopy (pega a copy mais recente
    // mesmo se swapHeaderLogo rodar de novo no resume). Labels/ícones têm default
    // alemão padrão quando a copy não os especifica.
    var ACC_DEFAULTS = {
      versand: { icon: "🚚", label: "Versand &amp; Rückgabe" },
      paket:   { icon: "📦", label: "Im Paket" },
    };
    function accMeta(which) {
      var d = ACC_DEFAULTS[which] || {};
      var c = (__brandCopy && __brandCopy.pdp && __brandCopy.pdp[which]) || null;
      if (!c || !c.html) return null;
      return { icon: c.icon || d.icon || "", label: c.label || d.label || "", html: c.html };
    }

    // Inject the shared accordion stylesheet once. Scoped to
    // `.phantom-accordion` so it styles BOTH the converted native
    // accordions and the fallback-injected ones identically.
    function ensureAccordionStyles() {
      if (document.getElementById("phantom-accordion-styles")) return;
      var style = document.createElement("style");
      style.id = "phantom-accordion-styles";
      style.textContent = [
        "details.phantom-accordion{border-bottom:1px solid rgba(128,128,128,0.2);padding:16px 0;margin:0;transition:all .2s ease;}",
        "details.phantom-accordion:last-child{border-bottom:none;}",
        // IMPORTANT: keep the <summary> itself as display:block. iOS Safari
        // does NOT toggle a <details> when its <summary> is display:flex/grid
        // (WebKit bug) — that's why Versand/Im Paket wouldn't open on iPhone.
        // The flex layout lives on an inner .phantom-acc-row wrapper instead.
        "details.phantom-accordion>summary{display:block;list-style:none;cursor:pointer;outline:none;font-weight:600;font-size:15px;color:currentColor;-webkit-tap-highlight-color:transparent;}",
        "details.phantom-accordion>summary::-webkit-details-marker{display:none!important;}",
        "details.phantom-accordion>summary::marker{display:none!important;content:'';}",
        "details.phantom-accordion .phantom-acc-row{display:flex;justify-content:space-between;align-items:center;gap:12px;}",
        "details.phantom-accordion>summary:hover{opacity:.85;}",
        "details.phantom-accordion[open] .phantom-icon-chevron{transform:rotate(180deg);}",
        "details.phantom-accordion>.phantom-acc-body{margin-top:14px;font-size:14px;color:currentColor;opacity:.85;line-height:1.6;padding-left:28px;padding-bottom:4px;}",
      ].join("");
      document.head.appendChild(style);
    }

    // Build the inner HTML of a phantom accordion: a <summary> (icon +
    // title + chevron) followed by the content <div> — both DIRECT
    // children of <details> so native open/close collapses the body.
    function accordionInner(which) {
      var m = accMeta(which);
      if (!m) return "";
      return (
        // The flex row is an inner <span>, NOT the <summary> itself — see the
        // iOS Safari note in ensureAccordionStyles. Summary stays display:block
        // so the native <details> toggle works on iPhone.
        "<summary>" +
        '<span class="phantom-acc-row">' +
        '<span style="display:flex;align-items:center;gap:10px;">' +
        '<span style="font-size:18px;line-height:1;">' + m.icon + "</span>" +
        "<span>" + m.label + "</span>" +
        "</span>" +
        '<span class="phantom-icon-chevron" style="transition:transform .3s ease;display:flex;align-items:center;color:currentColor;opacity:.6;flex:0 0 auto;">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>' +
        "</span>" +
        "</span>" +
        "</summary>" +
        '<div class="phantom-acc-body">' + m.html + "</div>"
      );
    }

    // Convert a native theme <details> into a clean phantom accordion in
    // place. We wipe its innerHTML and rebuild summary + body as direct
    // children — this fixes the theme's grid/flex content wrapper that was
    // spilling the copy into a two-column layout and breaking the collapse.
    function convertDetails(d, which) {
      try {
        var wasOpen = d.hasAttribute("open");
        // On this theme the accordion's content is a SIBLING of the <details>
        // (.accordion__content-wrapper / .accordion__content), NOT a child.
        // Replacing the <details> innerHTML alone left the theme's ORIGINAL
        // copy (clean "14 Tage / kontakt@cityoutlet.de" text) rendered right
        // below ours = the duplicate. Remove those sibling content nodes.
        var sib = d.nextElementSibling;
        while (sib && /accordion__content/.test(typeof sib.className === "string" ? sib.className : "")) {
          var next = sib.nextElementSibling;
          try { sib.parentNode.removeChild(sib); } catch (_e) {}
          sib = next;
        }
        d.classList.add("phantom-accordion");
        d.innerHTML = accordionInner(which);
        if (wasOpen) d.setAttribute("open", ""); else d.removeAttribute("open");
      } catch (_e) {}
    }

    function removeCustomContainer() {
      try {
        var e = document.getElementById("phantom-custom-accordions-container");
        if (e && e.parentNode) e.parentNode.removeChild(e);
      } catch (_e) {}
    }

    // Fallback for PDPs whose theme ships no native accordion at all.
    function injectCustomContainer() {
      if (document.getElementById("phantom-custom-accordions-container")) return;
      // Insert as a clean sibling after the description/form so we don't
      // nest inside theme-specific flex columns that would break heights.
      var target = document.querySelector(".product__description") ||
                   document.querySelector("form[action*='/cart/add']") ||
                   document.querySelector(".product-form") ||
                   document.querySelector(".product-single__meta") ||
                   document.querySelector(".product-info") ||
                   document.querySelector(".product__info-container");
      if (!target) return;
      var versandInner = accordionInner("versand");
      var paketInner = accordionInner("paket");
      if (!versandInner && !paketInner) return; // nada pra injetar
      var container = document.createElement("div");
      container.id = "phantom-custom-accordions-container";
      container.style.cssText = "margin-top:25px;margin-bottom:25px;border-top:1px solid rgba(128,128,128,0.2);border-bottom:1px solid rgba(128,128,128,0.2);font-family:inherit;width:100%;display:block;clear:both;";
      container.innerHTML =
        (versandInner ? '<details class="phantom-accordion" open>' + versandInner + "</details>" : "") +
        (paketInner ? '<details class="phantom-accordion">' + paketInner + "</details>" : "");
      if (target.classList.contains("product__description") ||
          target.tagName === "FORM" ||
          target.classList.contains("product-form") ||
          target.classList.contains("product-form__buttons")) {
        if (target.parentNode) target.parentNode.insertBefore(container, target.nextSibling);
      } else {
        target.appendChild(container);
      }
    }

    // De-dropdown the PRODUCT DESCRIPTION accordion (the theme's
    // `Beschreibung` / product-title accordion, Material icon "description").
    // The merchant wants the description shown inline — always open, no
    // chevron, not clickable — while Versand & Rückgabe and Im Paket stay as
    // collapsible dropdowns. We force the <details> open, keep it open even
    // if the theme's accordion JS toggles it, and reveal the content (which
    // on this theme is a SIBLING `.accordion__content`, not a child).
    function inlineDescription() {
      if (!document.getElementById("phantom-desc-inline-style")) {
        var st = document.createElement("style");
        st.id = "phantom-desc-inline-style";
        st.textContent = [
          // Hide the description accordion header entirely — it just repeats
          // the product title (already shown as the H1 at the top), so it's
          // redundant. The description content (which starts at the first
          // fragrance, e.g. "Versace DYLAN BLUE – 100 ml") stays inline.
          "details[data-phantom-desc-inline] > summary{display:none!important}",
          // Force the content open regardless of [open] state / theme JS.
          "details[data-phantom-desc-inline] .accordion__content,details[data-phantom-desc-inline] + .accordion__content,details[data-phantom-desc-inline] + .accordion__content-wrapper,[data-phantom-desc-parent] .accordion__content,[data-phantom-desc-parent] .accordion__content-wrapper{display:block!important;max-height:none!important;overflow:visible!important;visibility:visible!important;opacity:1!important}",
        ].join("");
        document.head.appendChild(st);
      }
      var list = document.querySelectorAll("details.accordion__details,details.product__accordion,details");
      for (var i = 0; i < list.length; i++) {
        var d = list[i];
        if (d.classList.contains("phantom-accordion")) continue; // our Versand/Paket
        if (d.getAttribute("data-phantom-desc-inline") === "1") continue;
        var s = d.querySelector("summary");
        if (!s) continue;
        var sumTxt = (s.textContent || "").toLowerCase();
        // Never touch the shipping/package dropdowns.
        if (sumTxt.indexOf("versand") !== -1 || sumTxt.indexOf("rückgabe") !== -1 || sumTxt.indexOf("ruckgabe") !== -1 || sumTxt.indexOf("paket") !== -1) continue;
        var icon = s.querySelector(".material-icon,.material-symbols-outlined");
        var isDesc = (icon && (icon.textContent || "").trim().toLowerCase() === "description") || !!s.querySelector(".accordion__title") || sumTxt.indexOf("beschreibung") !== -1;
        if (!isDesc) continue;
        d.setAttribute("data-phantom-desc-inline", "1");
        // Mark the wrapping `.product__accordion` so its content (which on
        // this theme is a SIBLING `.accordion__content-wrapper`, not a child
        // of the <details>) is force-revealed even with the summary hidden.
        try {
          var wrap = d.closest(".product__accordion, .accordion");
          if (wrap) wrap.setAttribute("data-phantom-desc-parent", "1");
        } catch (_e) {}
        try { d.open = true; d.setAttribute("open", ""); } catch (_e) {}
        try { d.addEventListener("toggle", function (ev) { if (!ev.target.open) { try { ev.target.open = true; } catch (_e2) {} } }); } catch (_e) {}
      }
    }

    function applyAccordions() {
      if (PHANTOM_HALT) return; // circuit breaker — querySelectorAll("details") é caro num storm
      ensureAccordionStyles();
      // inlineDescription() disabled — the force-open of the theme's
      // description accordion was fragile across mobile browsers (rendered a
      // tall blank gap on iOS Chrome). The description stays a native dropdown.
      // Track which keys we've already rendered THIS pass so a theme that
      // ships duplicate accordions (or mobile+desktop copies) only ever
      // shows one Versand and one Paket block.
      var rendered = { versand: false, paket: false };
      var detailsList = document.querySelectorAll("details");
      for (var i = 0; i < detailsList.length; i++) {
        var d = detailsList[i];
        var summary = d.querySelector("summary");
        if (!summary) continue;
        var title = (summary.textContent || "").trim().toLowerCase();
        var which = null;
        if (title.indexOf("versand") !== -1 || title.indexOf("rückgabe") !== -1 || title.indexOf("ruckgabe") !== -1) which = "versand";
        else if (title.indexOf("paket") !== -1) which = "paket";
        if (!which) continue;
        // Sem copy branded pra essa chave → não toca no acordeão nativo do tema.
        if (!accMeta(which)) continue;

        if (rendered[which]) {
          // Duplicate of an already-rendered key — hide it.
          try { d.style.display = "none"; } catch (_e) {}
          continue;
        }
        rendered[which] = true;
        if (d.getAttribute("data-phantom-applied") !== which) {
          convertDetails(d, which);
          d.setAttribute("data-phantom-applied", which);
        }
      }

      // A native accordion exists → drop the injected fallback if present.
      if (rendered.versand || rendered.paket) {
        removeCustomContainer();
      } else if (window.location.pathname.indexOf("/products/") !== -1) {
        // No native accordion on this PDP — inject our own.
        injectCustomContainer();
      } else {
        // Soft-nav saiu do PDP → remove qualquer container injetado que sobrou.
        removeCustomContainer();
      }
    }
    applyAccordions();
    // Wire o observer/interval só uma vez (swapHeaderLogo pode rodar de novo no
    // resume) — applyAccordions acima já re-renderiza com a copy mais recente.
    if (__accWired) return;
    __accWired = true;
    // Respeita o circuit breaker (desconecta/limpa quando PHANTOM_HALT trip) —
    // applyAccordions faz querySelectorAll("details") no doc todo a cada mutação.
    if (typeof MutationObserver === "function") {
      try {
        var mo = new MutationObserver(function () {
          if (PHANTOM_HALT) { try { mo.disconnect(); } catch (_e) {} return; }
          applyAccordions();
        });
        mo.observe(document.body, { subtree: true, childList: true });
      } catch (_e) {}
    }
    var accTimer = setInterval(function () {
      if (PHANTOM_HALT) { try { clearInterval(accTimer); } catch (_e) {} return; }
      applyAccordions();
    }, 1500);
  }

  // Inject a "Katalog" entry into the hamburger / main nav that points
  // at the camouflaged outlet collection — same UTM stays in the URL so
  // the destination keeps swap-mode active.
  function installCatalogLink() {
    var BASE_URL = "/collections/bestsellers?utm_phantom=" + encodeURIComponent(TOKEN) +
      "&utm_source=ad&utm_medium=paid";
    var SHOPIFY_URL = BASE_URL; // same — kept as the canonical href
    function alreadyInjected(menu) {
      return !!menu.querySelector("[data-phantom-catalog-link]");
    }
    function buildLink(extraClass, innerWrap) {
      var classes = "phantom-catalog-link " + (extraClass || "");
      var inner = innerWrap || function (t) { return t; };
      return (
        '<a href="' + SHOPIFY_URL + '" class="' + classes + '" data-phantom-catalog-link="1">' +
        inner("Katalog") +
        "</a>"
      );
    }
    function injectInto(menu) {
      if (!menu || alreadyInjected(menu)) return;
      // Dawn / Horizon menu shapes:
      //   <ul class="menu-drawer__menu"> <li class="menu-drawer__menu-item"><a/></li> ... </ul>
      //   <ul class="header__inline-menu-list"> <li><a class="header__menu-item"/></li> </ul>
      var list = menu.querySelector(
        ".menu-drawer__menu," +
        ".menu-drawer__navigation > ul," +
        ".header__inline-menu," +
        "ul.list-menu," +
        "ul[role='list']," +
        "ul"
      );
      if (!list) return;
      // Try to copy classes from an existing <li>; if the menu is empty
      // (the merchant hasn't configured nav links yet) fall back to the
      // canonical Dawn classes so our entry still renders correctly.
      var sample = list.querySelector("li");
      var liClass = sample ? (sample.getAttribute("class") || "") : "menu-drawer__menu-item-wrapper";
      var aSample = sample ? sample.querySelector("a") : null;
      var aClass = aSample
        ? (aSample.getAttribute("class") || "")
        : "menu-drawer__menu-item list-menu__item link link--text focus-inset";
      var li = document.createElement("li");
      if (liClass) li.className = liClass;
      li.setAttribute("data-phantom-catalog-li", "1");
      li.innerHTML =
        '<a href="' + SHOPIFY_URL + '" class="' + aClass + ' phantom-catalog-link" data-phantom-catalog-link="1">' +
        '<span>Katalog</span></a>';
      list.insertBefore(li, list.firstChild);
    }
    function run() {
      var candidates = document.querySelectorAll(
        "menu-drawer," +
        ".menu-drawer," +
        ".header__drawer," +
        ".drawer--menu," +
        "header nav," +
        "nav.header__inline-menu"
      );
      for (var i = 0; i < candidates.length; i++) injectInto(candidates[i]);
    }
    run();
    // Re-inject when the hamburger drawer renders lazily — but GUARD against the
    // infinite loop that FROZE some PDPs: our insertBefore is itself a DOM mutation,
    // and the theme's <menu-drawer> custom element (secondary.js) re-renders the nav
    // in response and drops our link, so the old un-guarded MutationObserver(run)
    // ping-ponged forever between this and secondary.js. Fixes: (1) bail the instant
    // the link exists ANYWHERE (global guard) so a successful inject stops all work;
    // (2) debounce; (3) hard-cap total re-injects then disconnect.
    if (typeof MutationObserver === "function") {
      try {
        var catT = null, catRuns = 0, catMo;
        catMo = new MutationObserver(function () {
          if (document.querySelector("[data-phantom-catalog-link]")) return;
          if (catT) return;
          if (catRuns++ > 60) { try { catMo.disconnect(); } catch (_e) {} return; }
          catT = setTimeout(function () { catT = null; try { run(); } catch (_e) {} }, 250);
        });
        catMo.observe(document.body, { subtree: true, childList: true });
      } catch (_e) {}
    }
  }

  function installDirectCheckout() {
    // We DON'T rewrite the cart-icon link in the header anymore — that
    // must open the side drawer (the visitor expects a quick peek at
    // their cart, not a checkout jump). We also leave alone any link
    // that lives inside the drawer itself ("Auschecken" already goes
    // to /checkout natively).
    //
    // What we DO intercept:
    //   - Submission of /cart/add forms when the visitor clicks the
    //     primary product CTA on the PDP. We re-fire the same request
    //     via fetch() so the theme's cart-update events fire, then we
    //     open the drawer (no auto-redirect to /checkout).
    function hookAddToCart() {
      document.addEventListener("submit", function (e) {
        var form = e.target;
        if (!form || form.tagName !== "FORM") return;
        var action = (form.action || "").toLowerCase();
        if (!/\/cart\/add(\.js)?$/.test(action)) return;
        // Intercept the form so the native submit doesn't navigate away.
        // The catching-bundle widget hooks the same /cart/add.js call
        // and pops the drawer on success, so all we need to do here is
        // make sure the request actually goes through.
        e.preventDefault();
        var fd = new FormData(form);
        fetch("/cart/add.js", { method: "POST", body: fd, credentials: "same-origin" })
          .catch(function () {});
      }, true);
    }
    // hookAddToCart();
  }

  // Blur product images on collection grids and on the PDP "you may also
  // like" recommendations, and overlay a yellow CTA badge that reads
  // "UP TO 50% OFF". Curiosity hook for ad traffic — they see there's a
  // discount but the actual product is teased.
  function installCardBlur() {
    var STYLE_ID = "phantom-card-blur-styles";
    if (!document.getElementById(STYLE_ID)) {
      var s = document.createElement("style");
      s.id = STYLE_ID;
      s.textContent = [
        // No blur — full branded swap (title + image) handles the camouflage.
        // We only paint a small corner badge so paid traffic sees the
        // discount upfront without occluding the branded product imagery.
        '.template-collection .card-wrapper,',
        '.template-collection .product-card,',
        '.template-collection .grid__item .card,',
        'product-recommendations .card-wrapper,',
        '.product-recommendations .card-wrapper,',
        '.related-products .card-wrapper {',
        '  position: relative;',
        '}',
        '.phantom-card-cta {',
        '  position: absolute;',
        '  top: 8px;',
        '  left: 8px;',
        '  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);',
        '  color: #0a0a0a;',
        '  font-weight: 800;',
        '  font-size: 10.5px;',
        '  letter-spacing: 0.05em;',
        '  padding: 5px 9px;',
        '  border-radius: 999px;',
        '  text-transform: uppercase;',
        '  font-family: -apple-system, BlinkMacSystemFont, "Inter", sans-serif;',
        '  box-shadow: 0 4px 12px -4px rgba(0,0,0,0.4);',
        '  pointer-events: none;',
        '  z-index: 10;',
        '  white-space: nowrap;',
        '}',
        '@media (max-width: 768px) {',
        '  .phantom-card-cta { font-size: 9.5px; padding: 4px 7px; }',
        '}',
      ].join("\n");
      document.head.appendChild(s);
    }

    function paintBadges() {
      var selectors = [
        ".template-collection .card-wrapper",
        ".template-collection .product-card",
        ".template-collection .grid__item .card",
        "product-recommendations .card-wrapper",
        ".product-recommendations .card-wrapper",
        ".related-products .card-wrapper",
      ];
      var nodes = document.querySelectorAll(selectors.join(","));
      for (var i = 0; i < nodes.length; i++) {
        var card = nodes[i];
        if (card.querySelector(":scope > .phantom-card-cta")) continue;
        var badge = document.createElement("div");
        badge.className = "phantom-card-cta";
        badge.textContent = "UP TO 50% OFF";
        card.appendChild(badge);
      }
    }

    paintBadges();
    if (typeof MutationObserver !== 'undefined') {
      try {
        var moTimeout = null;
        var mo = new MutationObserver(function() { 
          if (moTimeout) clearTimeout(moTimeout);
          moTimeout = setTimeout(function() { paintBadges(); }, 150);
        });
        mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
      } catch (_e) {}
    }
    // Re-run on a slow heartbeat so AJAX-injected recommendations and
    // infinite-scroll batches always get the badge.
    setInterval(paintBadges, 1200);
  }

  // The Shopify GDPR cookie-consent banner (.shopify-pc__banner) renders as a
  // CENTER MODAL (z-index 2000000) over the product on EU/DE stores and
  // SWALLOWS ALL TAPS until dismissed — visitors couldn't click anything. On
  // the ad path we auto-accept it (records consent, enables tracking pixels,
  // and removes the click-blocker). Falls back to hiding it if no button.
  function dismissCookieBanner() {
    try {
      var ban = document.querySelector(".shopify-pc__banner");
      if (!ban) return;
      var btn = ban.querySelector(".shopify-pc__banner__btn-accept");
      if (!btn) {
        var bs = ban.querySelectorAll("button,a");
        for (var i = 0; i < bs.length; i++) {
          var t = (bs[i].textContent || "");
          if (/akzept|accept|aceit|zustimm|allow/i.test(t) && !/ablehn|decline|verwalt|einstell|manage|recus/i.test(t)) { btn = bs[i]; break; }
        }
      }
      if (btn) { btn.click(); return; }
      ban.style.setProperty("display", "none", "important");
      var ov = document.querySelector(".shopify-pc__overlay");
      if (ov) ov.style.setProperty("display", "none", "important");
    } catch (_e) {}
  }

  // ---------- Horizon / Shrine soft-navigation handler ----------
  // On a soft nav (collection link, facet, sort, pagination, predictive
  // search, view-transition, back/forward) Shopify section-rendering injects
  // new clean HTML WITHOUT re-running any <head> inline script, so nothing
  // re-hides the page and it flashes clean until the 1000ms poll. We hook
  // history + the theme's own events: re-hide instantly, recompute the route,
  // run the synchronous (snapshot-backed, zero-network) swap, then guarantee
  // a bounded reveal. Idempotent + cheap; safe to fire on every nav.
  var softNavTimer = null;
  function reHideForSoftNav() {
    try {
      var h = document.documentElement;
      if (h && h.className.indexOf("phantom-loading") === -1) {
        // Only re-hide if #phantom-init style still exists (its CSS rule is
        // what the class drives). If the kill-switch removed the style we
        // must NOT add the class (it would do nothing AND risk a stuck class
        // with no exit). Re-inject a minimal hide style so the class works.
        if (!document.getElementById("phantom-init")) {
          try {
            var st = document.createElement("style");
            st.id = "phantom-init";
            st.textContent = "html.phantom-loading body{visibility:hidden!important}html.phantom-loading{background:#fff!important}";
            (document.head || document.documentElement).appendChild(st);
          } catch (_e) {}
        }
        h.className = (h.className ? h.className + " " : "") + "phantom-loading";
      }
    } catch (_e) {}
  }
  function onSoftNav() {
    // Recompute page-type for the new route.
    try {
      path = location.pathname;
      productMatch = path.match(/\/products\/([^\/?#]+)/);
      collectionMatch = path.match(/^\/collections\/([^\/?#]+)/);
      isIndex = path === "/" || path === "";
      isSearch = path === "/search";
      isList = collectionMatch || isIndex || isSearch || /^\/products$/.test(path);
    } catch (_e) {}
    // Re-hide immediately so the incoming clean section is not visible.
    reHideForSoftNav();
    if (softNavTimer) clearTimeout(softNavTimer);
    // Apply branded content from the in-memory snapshot (synchronous, no
    // network) as soon as the new DOM lands, then reveal. We retry on a tiny
    // ramp because section-render may not have committed the DOM yet on the
    // first tick.
    function pass() {
      try {
        if (productMatch) {
          var snapMain = snapshotItem(productMatch[1]);
          if (snapMain) applyProduct(snapMain);
        }
        applyImagesToGallery();
      } catch (_e) {}
      try { swapAllCards(productMatch ? productMatch[1] : null).catch(function () {}); } catch (_e) {}
      try { swapHeaderLogo(); } catch (_e) {}
    }
    pass();
    setTimeout(pass, 60);
    setTimeout(pass, 180);
    setTimeout(pass, 400);
    // GUARANTEED bounded reveal — strip the hide no later than 700ms after the
    // nav even if everything above failed. Never a permanent hang.
    softNavTimer = setTimeout(function () {
      try { killLoader(); } catch (_e) {}
    }, 700);
    // Also reveal fast on the happy path: once the snapshot swap ran (cards
    // are branded), reveal on the next frame.
    setTimeout(function () { try { killLoader(); } catch (_e) {} }, 120);
  }
  function installSoftNavHook() {
    try {
      var wrap = function (orig) {
        return function () {
          var r = orig.apply(this, arguments);
          try { onSoftNav(); } catch (_e) {}
          return r;
        };
      };
      if (history && history.pushState) history.pushState = wrap(history.pushState);
      if (history && history.replaceState) history.replaceState = wrap(history.replaceState);
    } catch (_e) {}
    try { window.addEventListener("popstate", function () { onSoftNav(); }); } catch (_e) {}
    // Shopify section-rendering + Horizon custom events that signal new DOM.
    try {
      var evts = ["shopify:section:load", "page:loaded", "page:change", "navigation:end"];
      for (var e = 0; e < evts.length; e++) {
        document.addEventListener(evts[e], function () { onSoftNav(); });
      }
    } catch (_e) {}
  }

  function startup() {
    if (productMatch) {
      var mainHandle = productMatch[1];
      // Hide the gallery images until the branded src is stable — kills the
      // slider-reinit flicker that only happens on the PDP. Runs first so the
      // hold is in place before any swap/slider churn paints.
      installGalleryHold();
      // Instant hero from the (light) snapshot if present — brands title + images
      // above the fold with ZERO network so the PDP never flashes the clean hero
      // on a cold connection. The network fetchSingle below still runs to fill the
      // full branded description (below the fold, no visible flash).
      try { var snapMain = snapshotItem(mainHandle); if (snapMain) applyProduct(snapMain); } catch (_e) {}
      // CRITICAL: every promise inside Promise.all must have its own
      // catch() — otherwise an unhandled rejection (e.g. r.json() on a
      // malformed response) hangs the whole chain forever and the
      // loader never tears down. See agent audit notes.
      Promise.all([
        logoTask,
        fetchSingle(mainHandle)
          .then(function (data) {
            if (data && data.ok && data.real) applyProduct(data.real);
          })
          .catch(function () { /* never let this branch hang the loader */ }),
        swapAllCards(mainHandle).catch(function () {}),
      ]).then(done, done);
    } else {
      Promise.all([logoTask, swapAllCards(null)]).then(done, done);
    }
    // Horizon soft-nav: re-hide + re-swap + bounded reveal on every in-store
    // navigation that does NOT re-run the <head> inline scripts. This is the
    // primary fix for the clean-flash 'ainda quebra' on navigation.
    installSoftNavHook();
    // Watch for AJAX-injected cards (Shopify product recommendations,
    // infinite scroll, predictive search).
    startObserver();
    // Watch for theme lazy-loaders rewriting `src` back to the clean image
    // after we swap — re-apply the branded URL when that happens.
    startSrcGuard();
    // Auto-dismiss the Shopify cookie-consent modal (it blocks all taps on
    // mobile). The banner is injected async, so retry for a few seconds.
    dismissCookieBanner();
    for (var cb = 1; cb <= 12; cb++) { (function (d) { setTimeout(dismissCookieBanner, d); })(cb * 400); }
    // Force cart → checkout direct routing on the ad path (skip /cart page).
    installDirectCheckout();
    // Blur collection cards + product recommendations and overlay the
    // "UP TO 50% OFF" CTA badge.
    installCardBlur();
    // Drop the "Katalog" link into the hamburger / inline nav so the
    // visitor has a one-tap shortcut back to the camouflaged collection.
    installCatalogLink();
    // Footer/PDP copy (Über uns / Kontakt / Versand / Im Paket) agora é
    // data-driven por loja: swapHeaderLogo() recebe data.copy de
    // /api/phantom/branding e dispara swapFooterBrand()/swapPdpAccordions() com
    // a copy da PRÓPRIA loja. O gate cravado por shop (tdss3x → Metzingen) foi
    // removido — cada loja injeta a sua copy, sem vazar marca de outra.
    relocateStickyAtc();
    localizeSaleBadges();
    try { applyCartDrawerDiscount(); } catch (_e) {}
    // FB Pixel tracking removed at merchant request.

    // Polling fallback — Shopify's <product-recommendations> custom element
    // loads via section render and the MutationObserver sometimes misses the
    // batch. Re-scan every 800ms for ~28s; we never stop early so theme
    // lazy-loaders that fire ~10-15s in still get caught. Each tick also
    // re-applies cards from the data cache (cheap, no network).
    var excl = productMatch ? productMatch[1] : null;
    // Permanent polling — cheap when cache is warm (no network), guarantees
    // self-healing if a theme JS event re-renders a card after our swap.
    setInterval(function () {
      if (PHANTOM_HALT) return;
      swapAllCards(excl).catch(function () {});
      // NOTE: applyImagesToGallery() intentionally NOT re-run here. The gallery is
      // branded once via applyProduct; re-walking it every second re-poked the
      // theme slider's offsetHeight/ResizeObserver path and fed the reflow freeze
      // on the heaviest PDPs. startSrcGuard self-heals any single src reset
      // (src-only, no layout writes), so branding stays correct.
      try { applyBundles(); } catch (_e) {}
      relocateStickyAtc();
      localizeSaleBadges();
      try { applyCartDrawerDiscount(); } catch (_e) {}
    }, 1000);

    // Event-loop watchdog — the global circuit breaker. Measures scheduling lag:
    // if a setInterval(500) keeps firing far late, the main thread is saturated
    // (a theme animation storm phantom is fighting). After 2 strikes we trip
    // PHANTOM_HALT, which every heavy reactive callback (poll, scheduleRescan,
    // startSrcGuard, ...) checks and bails on — so phantom stops working and the
    // page recovers instead of freezing. The initial branded swap already ran, so
    // content stays branded; only late self-healing is given up. Cheap and never
    // a false-positive in normal operation (lag stays near 0).
    (function () {
      var wdLast = Date.now(), wdStrikes = 0;
      var wd = setInterval(function () {
        var now = Date.now(), lag = now - wdLast - 500;
        wdLast = now;
        if (lag > 700) {
          if (++wdStrikes >= 2) { PHANTOM_HALT = true; try { clearInterval(wd); } catch (_e) {} }
        } else { wdStrikes = 0; }
      }, 500);
    })();

    function onResume() {
      try {
        forceRepaint();
        killLoader();
        if (MAIN_PRODUCT_REAL) {
          applyProduct(MAIN_PRODUCT_REAL);
        }
        try { swapHeaderLogo(); } catch (_) {}
        dismissCookieBanner();
        window.dispatchEvent(new Event("resize"));
      } catch (_e) {}
    }

    try {
      window.addEventListener("pageshow", function (e) {
        onResume();
        if (e && e.persisted) {
          setTimeout(onResume, 100);
          setTimeout(onResume, 400);
        }
      });
      document.addEventListener("visibilitychange", function () {
        if (document.visibilityState === "visible") {
          onResume();
          setTimeout(onResume, 200);
        }
      });
      window.addEventListener("focus", function () {
        onResume();
        setTimeout(onResume, 200);
      });
    } catch (_e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startup, { once: true });
  } else {
    startup();
  }
})();
